import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import { usePermission } from '../../auth/usePermission';
import { useModuleEntitlements } from '../../hooks/useModuleEntitlements';
import { useTravelUi } from '../../hooks/useTravelUi';
import { getApiErrorMessage, isModuleNotEntitledError } from '../../utils/apiErrors';
import {
  Card,
  Grid,
  Divider,
  Modal,
  Box,
  TextField,
  CircularProgress,
  Chip,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  IconButton,
  Button,
  Typography,
  Autocomplete,
  Tooltip,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ThemedCustomStepper from 'components/CustomStepper';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import Activities from 'layouts/dashboard/components/Activities';
import Quotations from 'components/Quotations';
import QuotationModal from 'components/CustomModals/AddQuotationModal';
import AttachmentUploadModal from 'components/CustomModals/AttachmentUploadModal';
import Attachments from 'layouts/dashboard/components/Attachments';
import ActivityModal from 'components/CustomModals/CustomActivityModal';
import { formatDate, toLocalDateTimeApi } from 'utils/DateTimeConversion';
import { useTheme } from '@mui/material/styles';
import { serializeEmailAttachments } from 'utils/emailAttachments';
import Confetti from 'react-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import ClosedLostModal from 'components/CloseLostModal';
import DealDetailButtons from 'components/CustomHeaderButtons/DealCustomButtons';
import AiScoreChip from 'components/AiAssistant/AiScoreChip';
import NextBestActions from 'components/AiAssistant/NextBestActions';
import CustomFieldsSection from 'components/CustomFields/CustomFieldsSection';
import DataLoadingSpinner from 'components/Spinners/DataLoadingSpinner';
import ClosedWonConfirmModal from 'components/ClosedWonConfirmModal';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';

import colors from "assets/theme/base/colors";
import { downloadPrivateObject } from 'utils/S3';
import { Email, PictureAsPdf, Delete, FileCopy, Close, Edit, Save, ThreeSixty, RequestQuote, ShoppingCart, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import EmailModal from 'components/EmailModal/index.js';
import { Skeleton } from '@mui/material';
import AttachmentIcon from '@mui/icons-material/Attachment';
import EventIcon from '@mui/icons-material/Event';
import CreateIcon from '@mui/icons-material/Create';
import { useSnackbar } from 'notistack';
import UnauthorizedAccess from 'components/StyledAccessDenied';
import { useCurrency } from '../../context/CurrencyContext';
import { frontendLogger } from 'utils/logger';
import EchoComposer from 'components/EchoComposer/EchoComposer';
import MDUserAvatar from 'components/MDUserAvatar';
import { useCurrencyCodes } from 'hooks/useCurrencyCodes';
import StatusStepperConfirmDialog from 'components/StatusStepperConfirmDialog';
import { RecordCallProvider } from "components/v2/RecordCallContext";
import RecordPageV2, { TabStack } from "components/v2/record/RecordPageV2";
import { StatusBadge } from "layouts/crm/account360/RelatedList";
import ActivityTimeline from "layouts/crm/account360/ActivityTimeline";
import { relativeTime } from "layouts/crm/account360/format";
import { Briefcase, Building2, CalendarDays, CircleDollarSign, Clock, FileText, History, LayoutGrid, Package, Paperclip, Percent, UserRound, Users } from "lucide-react";
import UserLink from "components/v2/UserLink";
import { UiDetailSection } from "components/v2/record/DetailSection";


const safeLog = async (fn) => {
  try { await fn(); } catch (e) { console.error('Logging failed:', e); }
};

// Add capitalizeFirstLetter function at the top of the file after imports
const capitalizeFirstLetter = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const QuotationCard = ({ quotation, onClick, onDownload, onEmail, leadData, isLatest, dealStage, totalQuotations }) => {
  // Add states for both loading indicators
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadLoadingId, setDownloadLoadingId] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const { axiosInstance } = useAuth();
  const [isOrderLoading, setIsOrderLoading] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const { formatCurrency, currency: systemCurrency } = useCurrency();

  const getQuotationStatusPillSx = (status) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'draft') {
      return { bgcolor: '#F6D4A6', color: '#8A4B00' };
    }
    if (normalized === 'sent') {
      return { bgcolor: '#D6E7FF', color: '#0E4EA8' };
    }
    if (normalized === 'accepted') {
      return { bgcolor: '#DDF3E5', color: '#1F8A56' };
    }
    if (normalized === 'rejected') {
      return { bgcolor: '#FEE2E2', color: '#BA1A1A' };
    }
    return { bgcolor: '#E5E7EB', color: '#374151' };
  };

  // Show "Latest" badge for the most recently updated quotation
  const showLatestBadge = isLatest && totalQuotations > 1 && quotation.status !== 'Accepted' && quotation.status !== 'Rejected';

  // Format date (UTC-aware via shared helper — same as Audit Information)
  const formatQuoteDate = (dateString) => {
    if (!dateString) return '-';
    return formatDate(dateString, 'date') || '-';
  };

  // Common styles for action icons
  const actionIconStyle = {
    width: 28,
    height: 28,
    backgroundColor: 'rgba(0,0,0,0.04)',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.08)',
    },
    marginLeft: 0.5
  };

  const downloadInvoice = async (quotationId) => {
    if (isDownloading) {
      console.warn("Download already in progress");
      return;
    }

    setIsDownloading(true);
    setDownloadLoadingId(quotationId);

    try {
      // Add timeout to axios request to prevent hanging
      const response = await axiosInstance.get(`/quotations/pdf/${quotationId}`, {
        responseType: 'blob',
        timeout: 30000 // 30 second timeout
      });

      // Check if response is valid blob (PDF)
      if (response.data.type !== 'application/pdf') {
        const reader = new FileReader();
        reader.onload = function () {
          try {
            // Try to parse as JSON in case it contains error info
            const responseData = JSON.parse(reader.result);
            throw new Error(responseData.detail || "Invalid response format");
          } catch (parseError) {
            throw new Error("Server returned invalid data format");
          }
        };
        reader.readAsText(response.data);
        return;
      }

      // Create a URL for the blob
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

      // Create a temporary link element to trigger download
      const link = document.createElement('a');
      link.href = blobUrl;

      // Get file name from content-disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Quotation_${quotationId}.pdf`;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename=(.+)/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          filename = fileNameMatch[1].replace(/["']/g, '');
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);

      // Trigger download
      link.click();

      // Clean up
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);

      enqueueSnackbar("Quotation downloaded successfully", { variant: "success" });

    } catch (error) {
      console.error("Error downloading quotation:", error);

      // Check if there's a specific error message from the server
      let errorMessage = "Failed to download quotation";

      // Handle network errors specifically
      if (error.code === 'ECONNABORTED') {
        errorMessage = "Request timed out. The server is taking too long to respond.";
      } else if (error.message && error.message.includes('Network Error')) {
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (error.response) {
        // If the server responded with an error
        if (error.response.data) {
          try {
            // Try to extract error message from response data
            if (error.response.data instanceof Blob) {
              const reader = new FileReader();

              // Create a promise to handle the async blob reading
              await new Promise((resolve, reject) => {
                reader.onload = () => {
                  try {
                    const result = JSON.parse(reader.result);
                    if (result && result.detail) {
                      errorMessage = result.detail;
                    }
                    resolve();
                  } catch (e) {
                    // If it's not parseable JSON, just use the text
                    if (typeof reader.result === 'string' && reader.result.trim().length > 0) {
                      errorMessage = reader.result;
                    }
                    resolve();
                  }
                };
                reader.onerror = reject;
                reader.readAsText(error.response.data);
              });
            } else if (error.response.data.detail) {
              errorMessage = error.response.data.detail;
            } else if (typeof error.response.data === 'string') {
              // If response data is a string, use it as message
              errorMessage = error.response.data;
            }
          } catch (parseError) {
            console.error("Error parsing error response:", parseError);
          }
        }

        // Add status code if available
        if (error.response.status) {
          errorMessage += ` (Status: ${error.response.status})`;
        }
      } else if (error.message) {
        // Use the error message directly if available
        errorMessage = error.message;
      }

      enqueueSnackbar(errorMessage, { variant: "error" });

    } finally {
      setIsDownloading(false);
      setDownloadLoadingId(null);
    }
  };

  const handleEmailClick = async (e) => {
    e.stopPropagation(); // Prevent card click event
    if (onEmail) {
      try {
        await onEmail(quotation);
      } catch (error) {
        console.error('Error handling email:', error);
      }
    }
  };

  return (
    <Box
      onClick={() => onClick && onClick(quotation)}
      sx={{
        mb: 1,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        overflow: 'hidden',
        '&:hover': {
          transform: onClick ? 'translateY(-2px)' : 'none',
          boxShadow: onClick ? '0 4px 10px rgba(0,0,0,0.15)' : 'none',
          borderColor: 'rgba(0,0,0,0.12)',
        }
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1,
        borderBottom: quotation.status ? '1px solid rgba(0,0,0,0.05)' : 'none',
      }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#344767', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.35 }}>
            {quotation.quote_name || 'Unnamed Quote'}
          </Typography>
          {showLatestBadge && (
            <Tooltip title="This quotation will be marked as Accepted when deal is Closed Won">
              <Chip
                label="Latest"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  backgroundColor: '#e3f2fd',
                  color: '#1565C8',
                  '.MuiChip-label': { px: 0.75 },
                }}
              />
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" fontWeight="medium" sx={{ mr: 1.5, color: '#344767' }}>
            {formatCurrency(quotation.total_amount, quotation.currency || leadData?.currency || 'INR')}
          </Typography>
          <Chip
            label={quotation.status || 'No Status'}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 600,
              '.MuiChip-label': { px: 1 },
              ...getQuotationStatusPillSx(quotation.status),
            }}
          />
          <Tooltip title="Download PDF">
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDownloadingPdf(true);
                downloadInvoice(quotation.id)
                  .finally(() => setIsDownloadingPdf(false));
              }}
              disabled={isDownloadingPdf}
              sx={actionIconStyle}
            >
              {isDownloadingPdf ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PictureAsPdf sx={{ fontSize: 16, color: 'text.secondary' }} />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Send Email">
            <IconButton
              size="small"
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsSendingEmail(true);
                try {
                  await handleEmailClick(e);
                } finally {
                  setIsSendingEmail(false);
                }
              }}
              disabled={isSendingEmail}
              sx={actionIconStyle}
            >
              {isSendingEmail ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1,
        py: 0.75,
        bgcolor: 'rgba(0,0,0,0.02)'
      }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Created: {formatQuoteDate(quotation.created_at)}
        </Typography>
      </Box>
    </Box>
  );
};

const OrderCard = ({ order, onClick }) => {

  const { formatCurrency, currency: systemCurrency } = useCurrency();
  // Color mapping for different order statuses
  const statusColorMap = {
    'Draft': 'info',
    'Pending': 'warning',
    'Completed': 'success',
    'Cancelled': 'error'
  };

  // Format date
  const formatOrderDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  return (
    <Box
      onClick={() => onClick && onClick(order)}
      sx={{
        mb: 1,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        overflow: 'hidden',
        '&:hover': {
          transform: onClick ? 'translateY(-2px)' : 'none',
          boxShadow: onClick ? '0 4px 10px rgba(0,0,0,0.15)' : 'none',
          borderColor: 'rgba(0,0,0,0.12)',
        }
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1,
        borderBottom: order.status ? '1px solid rgba(0,0,0,0.05)' : 'none',
      }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#344767', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.35 }}>
            {order.order_number || 'Unnamed Order'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" fontWeight="medium" sx={{ mr: 1.5, color: '#344767' }}>
            {formatCurrency(order.total_amount, order.currency || systemCurrency || 'INR')}
          </Typography>
          <Chip
            label={order.status || 'No Status'}
            color={statusColorMap[order.status] || 'default'}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 600,
              '.MuiChip-label': { px: 1 },
              color: '#ffffff'
            }}
          />
        </Box>
      </Box>

      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1,
        py: 0.75,
        bgcolor: 'rgba(0,0,0,0.02)'
      }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Created: {formatOrderDate(order.created_at)}
        </Typography>
      </Box>
    </Box>
  );
};

const QuotationsList = ({ quotations, onQuotationClick, onDownloadPDF, onSendEmail, leadData, dealStage }) => {
  if (!quotations || quotations.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
          px: 2,
        }}
      >
        <Box sx={{
          width: 50,
          height: 50,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.04)',
          mb: 1.5
        }}>
          <RequestQuote sx={{ fontSize: 24, color: 'text.secondary' }} />
        </Box>
        <Typography variant="body2" color="text.secondary" align="center">
          No quotations to display.
        </Typography>
        <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
          Create your first quotation by clicking the "Add Quotation" button above.
        </Typography>
      </Box>
    );
  }

  // Sort quotations by updated_at to find the latest one
  const sortedQuotations = [...quotations].sort((a, b) =>
    new Date(b.updated_at) - new Date(a.updated_at)
  );
  const latestQuotationId = sortedQuotations[0]?.id;

  return (
    <Box
      sx={{
        maxHeight: quotations.length > 2 ? 250 : 'auto',
        overflowY: quotations.length > 2 ? 'auto' : 'visible',
        px: 2,
        py: 1.5,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '3px',
        }
      }}
    >
      {sortedQuotations.map((quotation) => (
        <QuotationCard
          key={quotation.id}
          quotation={quotation}
          onClick={onQuotationClick}
          onDownload={onDownloadPDF}
          onEmail={onSendEmail}
          leadData={leadData}
          isLatest={quotation.id === latestQuotationId}
          dealStage={dealStage}
          totalQuotations={sortedQuotations.length}
        />
      ))}
    </Box>
  );
};

const OrdersList = ({ orders, onOrderClick }) => {
  // For empty state
  if (!orders || orders.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
          px: 2,
        }}
      >
        <Box sx={{
          width: 50,
          height: 50,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.04)',
          mb: 1.5
        }}>
          <ShoppingCart sx={{ fontSize: 24, color: 'text.secondary' }} />
        </Box>
        <Typography variant="body2" color="text.secondary" align="center">
          No orders to display.
        </Typography>
        <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
          Create your first order by clicking the "Add Order" button above.
        </Typography>
      </Box>
    );
  }

  // Dynamic height based on number of orders
  const containerHeight = Math.min(250, orders.length * 80);

  return (
    <Box
      sx={{
        maxHeight: orders.length > 2 ? containerHeight : 'auto',
        overflowY: orders.length > 2 ? 'auto' : 'visible',
        pb: 0.5,
        pt: 2,
        px: 1.5,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '10px',
        },
      }}
    >
      {orders.map(order => (
        <OrderCard
          key={order.id}
          order={order}
          onClick={onOrderClick}
        />
      ))}
    </Box>
  );
};

function DealDetail() {
  const { id } = useParams();
  // const {currency: CurrencyFromContext} = useCurrency();
  const { user, axiosInstance } = useAuth();
  const { isSupportUser } = usePermission('deals');
  // UI v2 record layout (design-reference "Sales · Deal record").
  const isV2 = useTheme()?.pearlUi === 'v2';
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const hasTasksModule = isModuleEnabled('TASKS');
  const hasProductsModule = isModuleEnabled('PRODUCTS');
  // Travel UI needs the TRAVEL edition and the company travel toggle.
  const { showTravel, loading: travelUiLoading } = useTravelUi();
  // Legacy CRM Orders / Quotations are hidden when Travel is on (same as sidenav),
  // except in v2 where they live in the Sales app beside Travel.
  // While travel UI is still resolving, treat as "unknown" — do not fetch legacy.
  const showLegacyOrders = !travelUiLoading && (!showTravel || isV2);
  const showLegacyQuotations = !travelUiLoading && (!showTravel || isV2);
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [updatedFields, setUpdatedFields] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteRelated, setDeleteRelated] = useState(false);
  const [activities, setActivities] = useState([]);
  const [agents, setAgents] = useState([]);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [isQuotationModalOpen, setIsQuotationModalOpen] = useState(false);
  const [quotations, setQuotations] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [newQuotation, setNewQuotation] = useState({
    deal_id: id,
    total_amount: 0,
    status: 'Sent',
  })
  const [newActivity, setNewActivity] = useState({
    deal_id: id,
    type: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const dealStages = ['Qualification', 'Needs Analysis', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'];
  const [activeStep, setActiveStep] = useState(0);
  const [attachments, setAttachments] = useState([]);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [accountData, setAccountData] = useState(null);
  const [leadData, setLeadData] = useState(null);
  const [contactData, setContactData] = useState(null);
  const [showFinalStageWarning, setShowFinalStageWarning] = useState(false);
  const [pendingStageChange, setPendingStageChange] = useState(null);
  const [stageStepConfirm, setStageStepConfirm] = useState({ open: false, index: null });
  const [showClosedLostModal, setShowClosedLostModal] = useState(false);
  const [closedLostComment, setClosedLostComment] = useState('');
  const [pendingClosedLostChange, setPendingClosedLostChange] = useState(null);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [users, setUsers] = useState([]);
  const [showClosedWonModal, setShowClosedWonModal] = useState(false);
  const [quotationsSummary, setQuotationsSummary] = useState(null);
  const [pendingClosedWonChange, setPendingClosedWonChange] = useState(null);

  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactagents, setContactAgents] = useState([]);
  const [accountagents, setAccountAgents] = useState([]);
  const [lead, setLead] = useState(leadData || {});
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const [generatedOrderNumber, setGeneratedOrderNumber] = useState('');
  const [orders, setOrders] = useState([]);
  const [originalDeal, setOriginalDeal] = useState(null); // New state to hold original deal values
  const [editedFields, setEditedFields] = useState({});
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isCloneLoading, setIsCloneLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isOrderLoading, setIsOrderLoading] = useState(false);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [isClosedWonSaving, setIsClosedWonSaving] = useState(false);
  const [isActivitiesLoading, setIsActivitiesLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const customFieldsRef = useRef(null); // Added to state declarations
  // Get the edit parameter from URL
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const shouldStartEditing = searchParams.get('edit') === 'true';

  const [customFields, setCustomFields] = useState([]);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    content: ''
  });
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [emailContext, setEmailContext] = useState({});
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [isEmailSending, setIsEmailSending] = useState(false);
  // Add to the state declarations in DealDetail.js
  const [accountContacts, setAccountContacts] = useState([]);
  const [loadingAccountContacts, setLoadingAccountContacts] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const { formatCurrency, currency: systemCurrency } = useCurrency();
  const [sectionOrder, setSectionOrder] = useState([]);
  const [hiddenSections, setHiddenSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(true);

  // State for cached names
  const [nameCache, setNameCache] = useState({});

  // State for feed items
  const [feedItems, setFeedItems] = useState([]);
  const [isFeedLoading, setIsFeedLoading] = useState(false);

  // State for editing feed items
  const [editingFeedItem, setEditingFeedItem] = useState(null);
  const [editFeedContent, setEditFeedContent] = useState('');
  const [isEditingFeed, setIsEditingFeed] = useState(false);

  // State for echo modal
  const [isEchoModalOpen, setIsEchoModalOpen] = useState(false);
  const [newEchoContent, setNewEchoContent] = useState('');
  const [isCreatingEcho, setIsCreatingEcho] = useState(false);

  // State for delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);


  useEffect(() => {
    if (shouldStartEditing) {
      setIsEditing(true);
      // Clean up the URL without refreshing the page
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [shouldStartEditing]);

  useEffect(() => {
    fetchDealData();
    fetchAgents()
    fetchAttachments();
    fetchContactAgents();
    fetchAccountAgents();
    fetchFeedItems(); // Add feed items fetch
  }, [id]);

  useEffect(() => {
    if (!id) return;
    if (showLegacyQuotations) {
      fetchQuotations();
    } else {
      setQuotations([]);
      // fetchQuotations is what clears the page spinner; travel tenants skip
      // it, so without this their deal records never finished loading.
      if (!travelUiLoading) setIsLoading(false);
    }
    if (showLegacyOrders) {
      fetchOrders();
    } else {
      setOrders([]);
    }
  }, [id, showLegacyQuotations, showLegacyOrders, travelUiLoading]);

  useEffect(() => {
    if (entitlementsLoading || !id) return;
    if (hasTasksModule) {
      fetchActivities();
    } else {
      setActivities([]);
    }
    if (showLegacyQuotations && hasProductsModule) {
      fetchProducts();
    } else {
      setProducts([]);
    }
  }, [id, entitlementsLoading, hasTasksModule, hasProductsModule, showLegacyQuotations]);

  const fetchFeedItems = async () => {
    setIsFeedLoading(true);
    try {
      const response = await axiosInstance.get(`/feed-items/entity/deals/${id}`);
      setFeedItems(response.data || []);
    } catch (error) {
      console.error('Error fetching feed items:', error);
      setFeedItems([]);
    } finally {
      setIsFeedLoading(false);
    }
  };

  const handleEchoSubmit = async (feedItem) => {
    // Add the new feed item to the beginning of the list
    setFeedItems(prevItems => [feedItem, ...prevItems]);

    // Close the modal
    handleCloseEchoModal();

    // Show success message
    setSnackbar({
      open: true,
      message: 'Message posted successfully!',
      severity: 'success',
    });

    // Refresh the feed items to ensure we have the latest data
    await fetchFeedItems();
  };

  const handleEditFeedItem = async (editData) => {
    try {
      setIsEditingFeed(true);

      // Extract the feed item ID from the edit data
      const feedItemId = editData.feed_item_id;

      if (!feedItemId) {
        throw new Error('No feed item ID found in edit data');
      }

      console.log('=== EDIT FEED ITEM DEBUG ===');
      console.log('Feed Item ID:', feedItemId);
      console.log('Edit Data received:', editData);
      console.log('Object Type:', editData.object_type);
      console.log('Object ID:', editData.object_id);
      console.log('Body content:', editData.body);
      console.log('Mentions:', editData.mentions);

      // Make the PUT request to the correct endpoint with the feed item ID
      const response = await axiosInstance.put(`/feed-items/${feedItemId}`, {
        feeditem: {
          object_type: editData.object_type,
          object_id: editData.object_id,
          body: editData.body
          // Don't include mentions - backend will parse them from body automatically
        }
      });

      if (response.status === 200) {
        // Refresh the entire feed to get updated data and proper sorting
        await fetchFeedItems();

        setSnackbar({
          open: true,
          message: 'Message updated successfully!',
          severity: 'success',
        });

        // The EchoComposer will handle clearing the edit state via onSuccess callback
        // so we don't need to do it here
      }
    } catch (error) {
      console.error('Error updating feed item:', error);
      setSnackbar({
        open: true,
        message: 'Error updating message',
        severity: 'error',
      });
    } finally {
      setIsEditingFeed(false);
    }
  };

  const handleDeleteFeedItem = async (feedItemId) => {
    try {
      const response = await axiosInstance.delete(`/feed-items/${feedItemId}`);

      if (response.status === 200) {
        // Update the feed items state by removing the deleted item
        setFeedItems(prevItems => prevItems.filter(item => item.id !== feedItemId));

        setSnackbar({
          open: true,
          message: 'Message deleted successfully!',
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Error deleting feed item:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting message',
        severity: 'error',
      });
    }
  };

  const handleDeleteConfirm = (feedItem) => {
    setItemToDelete(feedItem);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirmOk = async () => {
    if (itemToDelete) {
      await handleDeleteFeedItem(itemToDelete.id);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const startEditFeedItem = (feedItem) => {
    setEditingFeedItem(feedItem.id);
    setEditFeedContent(feedItem.feeditem.body);
  };

  const cancelEditFeedItem = () => {
    setEditingFeedItem(null);
    setEditFeedContent('');
  };

  const handleOpenEchoModal = () => {
    setIsEchoModalOpen(true);
  };

  const handleCloseEchoModal = () => {
    setIsEchoModalOpen(false);
  };

  const handleCreateEcho = async () => {
    // This will be handled by EchoComposer's onSubmit
    handleCloseEchoModal();
  };

  // Component for rendering mentions with immediate name display
  const MentionDisplay = ({ mention, fullMatch, entityTypeOrUser, entityId, mentions, navigate }) => {
    // Calculate display name immediately without async operations
    let displayName = '';

    if (entityId) {
      // For entity mentions, we'll need to fetch the name
      // But for now, let's show the entity type and ID
      displayName = `${entityTypeOrUser}:${entityId}`;
    } else {
      // For user mentions, use entity_name immediately if available
      if (mention && mention.entity_name) {
        displayName = mention.entity_name;
      } else {
        // Fallback to username
        displayName = entityTypeOrUser;
      }
    }

    return (
      <Box
        component="span"
        sx={{
          color: entityId ? 'info.main' : 'primary.main',
          fontWeight: 'bold',
          backgroundColor: entityId ? 'rgba(33, 150, 243, 0.08)' : 'rgba(103, 58, 183, 0.08)',
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          mx: 0.25,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: entityId ? 'rgba(33, 150, 243, 0.15)' : 'rgba(103, 58, 183, 0.15)',
          }
        }}
        onClick={() => {
          if (entityId) {
            // Navigate to the mentioned entity
            navigate(`/${entityTypeOrUser.toLowerCase()}s/${entityId}`);
          } else {
            // Handle user mention - navigate to user profile
            if (mention && mention.entity_id) {
              navigate(`/users/${mention.entity_id}/profile`);
            }
          }
        }}
      >
        {displayName}
      </Box>
    );
  };

  // Helper function to extract name parts from created_by_name
  const extractNameParts = (createdByUser, createdByName) => {
    let nameParts = {
      firstName: 'U',
      middleName: '',
      lastName: '',
      avatarUrl: null
    };

    if (createdByUser?.first_name && createdByUser?.last_name) {
      nameParts = {
        firstName: createdByUser.first_name,
        middleName: createdByUser.middle_name || '',
        lastName: createdByUser.last_name,
        avatarUrl: createdByUser.avatar || null
      };
    } else if (createdByName) {
      const namePartsArray = createdByName.split(' ').filter(part => part.trim());
      if (namePartsArray.length === 1) {
        nameParts = {
          firstName: namePartsArray[0],
          middleName: '',
          lastName: '',
          avatarUrl: null
        };
      } else if (namePartsArray.length >= 2) {
        nameParts = {
          firstName: namePartsArray[0],
          middleName: namePartsArray.slice(1, -1).join(' '),
          lastName: namePartsArray[namePartsArray.length - 1],
          avatarUrl: null
        };
      }
    }

    return nameParts;
  };

  // Enhanced feed content parser that uses async name fetching
  const parseFeedContent = (feedItem) => {
    if (!feedItem?.feeditem?.body) return '';

    const body = feedItem.feeditem.body;
    const mentions = feedItem.feeditem.mentions || [];

    console.log('Parsing feed content:', { body, mentions });

    // Parse mentions in the format @username (for users) or @EntityType:entityId (for entities)
    const parts = [];
    const mentionRegex = /@([A-Za-z0-9_.@]+)(?::([A-Za-z0-9_]+))?/g;
    let lastIndex = 0;
    let match;

    // Create a map of usernames to mention data for better matching
    const userMentionsMap = {};
    mentions.forEach(mention => {
      if (mention.type === 'user' && mention.entity_name) {
        // Try to match by username pattern in the body
        const usernamePattern = new RegExp(`@${mention.entity_name.replace(/\s+/g, '[._]')}`, 'i');
        if (usernamePattern.test(body)) {
          userMentionsMap[mention.entity_name] = mention;
        }
      }
    });

    while ((match = mentionRegex.exec(body)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {body.substring(lastIndex, match.index)}
          </span>
        );
      }

      // Add mention as highlighted text with async name fetching
      const [fullMatch, firstPart, secondPart] = match;

      // Determine if this is a user mention or entity mention
      let entityTypeOrUser, entityId;

      if (secondPart) {
        // Format: @EntityType:entityId
        entityTypeOrUser = firstPart;
        entityId = secondPart;
      } else {
        // Format: @username (user mention)
        entityTypeOrUser = firstPart;
        entityId = null;
      }

      // Find the corresponding mention data
      let mentionData;
      if (entityId) {
        // For entity mentions: @EntityType:entityId
        mentionData = mentions.find(m => m.type === 'record' && m.entity_type === entityTypeOrUser && m.entity_id === entityId);
      } else {
        // For user mentions: @username - try multiple matching strategies
        // First, try exact match with entity_name
        mentionData = mentions.find(m => m.type === 'user' && m.entity_name === entityTypeOrUser);

        // If not found, try matching by username pattern
        if (!mentionData) {
          const userMention = mentions.find(m => {
            if (m.type === 'user' && m.entity_name) {
              // Check if the username in text matches the entity_name (with possible dots/underscores)
              const normalizedEntityName = m.entity_name.replace(/\s+/g, '[._]');
              const usernamePattern = new RegExp(`^${normalizedEntityName}$`, 'i');
              return usernamePattern.test(entityTypeOrUser);
            }
            return false;
          });
          if (userMention) {
            mentionData = userMention;
          }
        }

        // If still not found, try to find by position in the mentions array
        if (!mentionData) {
          const userMentions = mentions.filter(m => m.type === 'user');
          // Count how many user mentions we've seen so far in the text
          const textBeforeCursor = body.substring(0, match.index);
          const userMentionsBefore = (textBeforeCursor.match(/@[A-Za-z0-9_.@]+/g) || []).length;
          if (userMentionsBefore < userMentions.length) {
            mentionData = userMentions[userMentionsBefore];
          }
        }
      }

      parts.push(
        <MentionDisplay
          key={`mention-${match.index}`}
          mention={mentionData}
          fullMatch={fullMatch}
          entityTypeOrUser={entityTypeOrUser}
          entityId={entityId}
          mentions={mentions}
          navigate={navigate}
        />
      );

      lastIndex = match.index + fullMatch.length;
    }

    // Add remaining text
    if (lastIndex < body.length) {
      parts.push(
        <span key={`text-end`}>
          {body.substring(lastIndex)}
        </span>
      );
    }

    return parts;
  };


  // Function to fetch lead name
  const fetchLeadName = async (leadId) => {
    if (nameCache[`lead_${leadId}`]) {
      return nameCache[`lead_${leadId}`];
    }

    try {
      const response = await axiosInstance.get(`/leads/${leadId}`);
      const leadName = response.data.full_lead_name || `Lead #${leadId}`;
      setNameCache(prev => ({ ...prev, [`lead_${leadId}`]: leadName }));
      return leadName;
    } catch (error) {
      console.error('Error fetching lead name:', error);
      return `Lead #${leadId}`;
    }
  };

  // Function to fetch account name
  const fetchAccountName = async (accountId) => {
    if (nameCache[`account_${accountId}`]) {
      return nameCache[`account_${accountId}`];
    }

    try {
      const response = await axiosInstance.get(`/accounts/${accountId}`);
      const accountName = response.data.name || `Account #${accountId}`;
      setNameCache(prev => ({ ...prev, [`account_${accountId}`]: accountName }));
      return accountName;
    } catch (error) {
      console.error('Error fetching account name:', error);
      return `Account #${accountId}`;
    }
  };

  // Function to fetch deal name
  const fetchDealName = async (dealId) => {
    if (nameCache[`deal_${dealId}`]) {
      return nameCache[`deal_${dealId}`];
    }

    try {
      const response = await axiosInstance.get(`/deals/${dealId}`);
      const dealName = response.data.name || `Deal #${dealId}`;
      setNameCache(prev => ({ ...prev, [`deal_${dealId}`]: dealName }));
      return dealName;
    } catch (error) {
      console.error('Error fetching deal name:', error);
      return `Deal #${dealId}`;
    }
  };

  // Function to fetch contact name
  const fetchContactName = async (contactId) => {
    if (nameCache[`contact_${contactId}`]) {
      return nameCache[`contact_${contactId}`];
    }

    try {
      const response = await axiosInstance.get(`/contacts/${contactId}`);
      const contactName = `${response.data.first_name || ''} ${response.data.last_name || ''}`.trim() || `Contact #${contactId}`;
      setNameCache(prev => ({ ...prev, [`contact_${contactId}`]: contactName }));
      return contactName;
    } catch (error) {
      console.error('Error fetching contact name:', error);
      return `Contact #${contactId}`;
    }
  };

  // Function to fetch entity name based on type
  const fetchEntityName = async (entityType, entityId) => {
    switch (entityType.toLowerCase()) {
      case 'lead':
        return await fetchLeadName(entityId);
      case 'account':
        return await fetchAccountName(entityId);
      case 'deal':
        return await fetchDealName(entityId);
      case 'contact':
        return await fetchContactName(entityId);
      case 'order':
        return await fetchOrderName(entityId);
      case 'invoice':
        return await fetchInvoiceName(entityId);
      case 'quote':
        return await fetchQuoteName(entityId);
      case 'product':
        return await fetchProductName(entityId);
      case 'case':
        return await fetchCaseName(entityId);
      default:
        return `${entityType} #${entityId}`;
    }
  };

  // Function to fetch user name
  const fetchUserName = async (userId) => {
    if (nameCache[`user_${userId}`]) {
      return nameCache[`user_${userId}`];
    }

    try {
      const response = await axiosInstance.get(`/users/${userId}`);
      const userName = response.data.fullname || response.data.full_name || response.data.username;
      setNameCache(prev => ({ ...prev, [`user_${userId}`]: userName }));
      return userName;
    } catch (error) {
      console.error('Error fetching user name:', error);
      return `User ${userId}`;
    }
  };

  // Function to fetch order name
  const fetchOrderName = async (orderId) => {
    if (nameCache[`order_${orderId}`]) {
      return nameCache[`order_${orderId}`];
    }

    try {
      const response = await axiosInstance.get(`/orders/${orderId}`);
      const orderName = response.data.order_number || `Order #${orderId}`;
      setNameCache(prev => ({ ...prev, [`order_${orderId}`]: orderName }));
      return orderName;
    } catch (error) {
      console.error('Error fetching order name:', error);
      return `Order #${orderId}`;
    }
  };

  // Function to fetch invoice name
  const fetchInvoiceName = async (invoiceId) => {
    if (nameCache[`invoice_${invoiceId}`]) {
      return nameCache[`invoice_${invoiceId}`];
    }

    try {
      const response = await axiosInstance.get(`/invoices/${invoiceId}`);
      const invoiceName = `Invoice #${response.data.invoice_number || invoiceId}`;
      setNameCache(prev => ({ ...prev, [`invoice_${invoiceId}`]: invoiceName }));
      return invoiceName;
    } catch (error) {
      console.error('Error fetching invoice name:', error);
      return `Invoice #${invoiceId}`;
    }
  };

  // Function to fetch quote name
  const fetchQuoteName = async (quoteId) => {
    if (nameCache[`quote_${quoteId}`]) {
      return nameCache[`quote_${quoteId}`];
    }

    try {
      const response = await axiosInstance.get(`/quotations/${quoteId}`);
      const quoteName = response.data.quote_name || `Quote #${quoteId}`;
      setNameCache(prev => ({ ...prev, [`quote_${quoteId}`]: quoteName }));
      return quoteName;
    } catch (error) {
      console.error('Error fetching quote name:', error);
      return `Quote #${quoteId}`;
    }
  };

  // Function to fetch product name
  const fetchProductName = async (productId) => {
    if (nameCache[`product_${productId}`]) {
      return nameCache[`product_${productId}`];
    }

    try {
      const response = await axiosInstance.get(`/products/${productId}`);
      const productName = response.data.name || `Product #${productId}`;
      setNameCache(prev => ({ ...prev, [`product_${productId}`]: productName }));
      return productName;
    } catch (error) {
      console.error('Error fetching product name:', error);
      return `Product #${productId}`;
    }
  };

  // Function to fetch case name
  const fetchCaseName = async (caseId) => {
    if (nameCache[`case_${caseId}`]) {
      return nameCache[`case_${caseId}`];
    }

    try {
      const response = await axiosInstance.get(`/cases/${caseId}`);
      const caseName = response.data.case_number || `Case #${caseId}`;
      setNameCache(prev => ({ ...prev, [`case_${caseId}`]: caseName }));
      return caseName;
    } catch (error) {
      console.error('Error fetching case name:', error);
      return `Case #${caseId}`;
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axiosInstance.get('/users/');
        setUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  // Fetch accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await axiosInstance.get('/accounts/');
        setAccounts(response.data);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    };
    fetchAccounts();
  }, []);


  const fetchOrders = async () => {
    try {
      const response = await axiosInstance.get(`/orders/deal/${id}`); // Use the new endpoint
      setOrders(response.data || []); // Set the orders state with the fetched data
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]); // Set to empty array on error
    }
  };

  // Fetch contacts

  // Add this function in DealDetail.js
  const fetchAccountContacts = async (accountId) => {
    if (!accountId) {
      setAccountContacts([]);
      return;
    }

    setLoadingAccountContacts(true);
    try {
      const response = await axiosInstance.get(`/contacts/?account_id=${accountId}`);
      setAccountContacts(response.data);
    } catch (error) {
      console.error('Error fetching account contacts:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching contacts for selected account',
        severity: 'error',
      });
    } finally {
      setLoadingAccountContacts(false);
    }
  };

  // Add this useEffect in DealDetail.js
  useEffect(() => {
    if (updatedFields.account_id || deal?.account_id) {
      fetchAccountContacts(updatedFields.account_id || deal?.account_id);
    }
  }, [updatedFields.account_id, deal?.account_id]);

  const fetchContacts = async () => {
    try {
      const response = await axiosInstance.get('/contacts/');
      setContacts(response.data); // Store fetched contacts in the state
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  useEffect(() => {
    fetchContacts(); // Fetch contacts when the component mounts
  }, []);

  const fetchContactAgents = async () => {
    try {
      const response = await axiosInstance.get('/contacts/');
      setContactAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchAccountAgents = async () => {
    try {
      const response = await axiosInstance.get('/accounts/');
      setAccountAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const formatDateNoTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      // hour: '2-digit',
      // minute: '2-digit',
    });
  };

  const Orders = ({ orders, onClick }) => {
    const statusColorMap = {
      'Draft': 'info',
      'In Progress': 'warning',
      'Delivered': 'primary',
      'Cancelled': 'error',
      'Completed': 'success',
    };

    return (
      <MDBox>
        {orders.length === 0 ? (
          <MDTypography variant="body2" color="text" textAlign="left" m={2}>
            No orders to display.
          </MDTypography>
        ) : (
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {orders.slice(0, 3).map(order => (
              <Card
                key={order.id}
                onClick={() => onClick && onClick(order)}
                sx={{
                  width: '95%',
                  p: 2,
                  m: 1,
                  // mb: 2,
                  // mt: 2,
                  // maxWidth: '600px',
                  // alignSelf: 'center',
                  // marginTop: index === 0 ? 2 : 0,s
                  borderRadius: 3,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 15px rgba(0,0,0,0.15), 0 3px 6px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="h6" gutterBottom>
                    {order.order_number || 'Unnamed Order'}
                  </MDTypography>
                  <Chip
                    label={order.status || 'No Status'}
                    color={statusColorMap[order.status] || 'default'}
                    variant="outlined"
                  />
                </MDBox>

                <MDBox mt={2} display="flex" justifyContent="space-between">
                  <MDTypography variant="body2">
                    Total Amount: {new Intl.NumberFormat('en-IN', {
                      style: 'currency',
                      currency: 'INR'
                    }).format(order.total_amount || 0)}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    Order Date: {order.order_date ? formatDateNoTime(order.order_date) : 'No Date'}
                  </MDTypography>
                </MDBox>
              </Card>
            ))}
          </div>
        )}
      </MDBox>
    );
  };

  const handleCloneDeal = async () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to clone deals.',
        severity: 'error',
      });
      return; // Exit the function early
    }
    try {
      setIsCloneLoading(true);

      // Create a new object with all deal properties including custom fields
      const clonedDeal = {
        ...deal,
        stage: "Qualification",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: user.id,
        updated_by: user.id,
        created_by_name: user.username,
        updated_by_name: user.username,
        custom_fields: {}, // Initialize custom_fields object
      };

      // Copy custom fields (fields starting with cf_)
      Object.keys(deal).forEach(key => {
        if (key.startsWith('cf_')) {
          clonedDeal.custom_fields[key] = deal[key];
        }
      });

      delete clonedDeal.id; // Remove the original deal's ID

      const response = await axiosInstance.post('/deals/', clonedDeal);

      // Log the clone action
      await frontendLogger.logCreate('Deal', {
        id: response.data.id,
        action: 'clone',
        cloned_from: deal.id,
        user: user.username,
        timestamp: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: 'Deal cloned successfully!',
        severity: 'success',
      });
      navigate(`/deals/${response.data.id}`);
    } catch (error) {
      console.error('Error cloning deal:', error);
      setSnackbar({
        open: true,
        message: 'Error cloning deal. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsCloneLoading(false);
    }
  };

  // Add this component within the DealDetail.js file
  const DealDetailsSkeleton = () => (
    <MDBox p={3}>
      <Grid container spacing={3}>
        {/* Simulate Deal Information section */}
        {[...Array(6)].map((_, index) => (
          <Grid item xs={12} md={6} key={`skeleton-field-${index}`}>
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={40} />
          </Grid>
        ))}

        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
        </Grid>

        {/* Simulate Related Information section */}
        {[...Array(4)].map((_, index) => (
          <Grid item xs={12} md={6} key={`skeleton-related-${index}`}>
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={40} />
          </Grid>
        ))}

        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
        </Grid>

        {/* Simulate Audit Information section */}
        {[...Array(4)].map((_, index) => (
          <Grid item xs={12} md={6} key={`skeleton-audit-${index}`}>
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" width="100%" height={40} />
          </Grid>
        ))}
      </Grid>
    </MDBox>
  );


  const fetchDealData = async () => {
    setIsDetailsLoading(true); // Use details loading instead of global loading
    try {
      const response = await axiosInstance.get(`/deals/${id}`);
      const dealData = response.data;
      setDeal(dealData);

      // Fetch account data if account_id exists
      if (dealData.account_id) {
        await fetchAccountData(dealData.account_id);
      }

      // Fetch contact data if contact_id exists
      if (dealData.contact_id) {
        await fetchContactData(dealData.contact_id);
      }

      // Fetch lead data if lead_id exists
      if (dealData.lead_id) {
        await fetchLeadData(dealData.lead_id);
      }

      // Set the active step based on the deal stage
      const fetchedStep = dealStages.indexOf(dealData.stage);
      setActiveStep(fetchedStep >= 0 ? fetchedStep : 0);
    } catch (error) {
      console.error('Error initializing page:', error);
      if (error.response?.status === 403) {
        setIsUnauthorized(true);
        setSnackbar({
          open: true,
          message: "You don't have permission to access this Deal",
          severity: 'error',
        });
        // Optionally redirect after a delay
        setTimeout(() => {
          navigate('/deals');
        }, 3000);
      } else {
        setSnackbar({
          open: true,
          message: 'Error loading Deal data. Please try again later.',
          severity: 'error',
        });
      }
    } finally {
      setIsDetailsLoading(false);
    }
  };


  const fetchProducts = async () => {
    if (!hasProductsModule) {
      setProducts([]);
      return;
    }
    try {
      const response = await axiosInstance.get('/inventory/details/');
      // console.log(`Products: ${JSON.stringify(response.data)}`);

      setProducts(response.data);
    } catch (error) {
      if (!isModuleNotEntitledError(error)) {
        console.error('Error fetching products:', error);
      }
      setProducts([]);
    }
  };

  // const fetchActivities = async () => {
  //   setIsLoading(true);
  //   try {
  //     const response = await axiosInstance.get(`/deals/${id}/activities/`);
  //     setActivities(response.data || []);
  //   } catch (error) {
  //     console.error('Error fetching activities:', error);
  //     setActivities([]);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const fetchActivities = async () => {
    if (!hasTasksModule) {
      setActivities([]);
      return;
    }
    try {
      const response = await axiosInstance.get('/activities/', {
        params: {
          parent_table: 'deals',
          parent_table_id: id
        }
      });;
      setActivities(response.data || []);
    } catch (error) {
      if (!isModuleNotEntitledError(error)) {
        console.error('Error fetching activities:', error);
        setSnackbar({
          open: true,
          message: getApiErrorMessage(error, 'Error fetching activities'),
          severity: 'error',
        });
      }
      setActivities([]);
    }
  };

  const fetchQuotations = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/quotations/?deal_id=${id}`);
      // Filter quotations to ensure they belong to the current deal
      const filteredQuotations = response.data.filter(
        (quotation) => quotation.deal_id === parseInt(id)
      );
      setQuotations(filteredQuotations || []);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      setQuotations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductsChange = (products) => {
    setSelectedProducts(products);
  };

  const calculateTotalAmount = () => {
    return selectedProducts.reduce((total, product) => total + (product.price * product.quantity), 0);
  };

  //   const handleDiscardChanges = () => {
  //     setEditedFields({}); // Reset editedFields to an empty object
  //     setUpdatedFields({}); // Reset updatedFields to an empty object
  //     setDeal(originalDeal); // Reset deal to the original values
  //     setIsEditing(false); // Exit edit mode
  //   };

  const handleEditToggle = () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to edit deals.',
        severity: 'error',
      });
      return; // Exit the function early
    }

    if (isEditing) {
      // If already in edit mode, discard changes and exit edit mode
      handleDiscardChanges();
    } else {
      // Enter edit mode and store the original deal values
      setOriginalDeal(deal); // Store the original deal values
      setEditedFields({ ...deal }); // Initialize editedFields with the current deal data
      setIsEditing(true); // Toggle edit mode
    }
    setIsEditing(!isEditing); // Toggle edit mode
  };

  const handleDiscardChanges = () => {
    setIsEditing(false);
    setUpdatedFields({});
    fetchDealData();
    fetchActivities();
    fetchAttachments();
  };

  const handleFieldUpdate = (field, value) => {
    setUpdatedFields((prev) => {
      let updatedValue = value;

      // When account is changed, reset contact and fetch new contacts
      if (field === 'account_id') {
        return {
          ...prev,
          account_id: value,
          contact_id: '',
          contact_name: '',
        };
      }

      if (field === 'estimated_closure_date') {
        const selectedDate = new Date(value);
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        if (selectedDate < currentDate) {
          setFormErrors((prev) => ({
            ...prev,
            estimated_closure_date: 'Estimated Closure Date must be today or in the future.',
          }));
          return prev;
        } else {
          setFormErrors((prev) => {
            const { estimated_closure_date, ...rest } = prev;
            return rest;
          });
          updatedValue = value;
        }
      }

      // Handle contact selection with validation
      if (field === 'contact_id') {
        const selectedContact = accountContacts.find(contact => contact.id === value);
        if (selectedContact) {
          return {
            ...prev,
            contact_id: value,
            contact_name: selectedContact.first_name
              ? `${selectedContact.first_name} ${selectedContact.last_name}`
              : selectedContact.last_name
          };
        }
        return prev;
      }

      if (field === 'probability') {
        let numValue = Number(value);
        if (numValue > 100) {
          numValue = 100;
          updatedValue = 100;
          setSnackbar({
            open: true,
            message: 'Probability cannot exceed more than 100%. Value set to 100%.',
            severity: 'info',
          });
        }
        if (numValue < 0 || isNaN(numValue)) {
          numValue = 0;
          updatedValue = 0;
        }
        updatedValue = Math.round(numValue);
      }

      if (field === 'value') {
        const numValue = value === '' || value === null ? null : Number(value);
        if (numValue !== null && Number.isFinite(numValue)) {
          // Do not block keystrokes here (e.g. typing 0.75); enforce on save.
          updatedValue = Number(numValue.toFixed(2));
        }
      }

      if (field === 'assigned_to_name') {
        const selectedAgent = agents.find(agent => String(agent.id) === String(value));
        return { ...prev, assigned_to: value, assigned_to_name: selectedAgent ? (selectedAgent.fullname || selectedAgent.username) : '' };
      }

      if (field.startsWith('cf_')) {
        if (value === '' || value === undefined) {
          updatedValue = null;
        } else if (typeof value === 'string' && !isNaN(value)) {
          const numValue = parseFloat(value);
          updatedValue = Number.isFinite(numValue) ? Number(numValue.toFixed(2)) : value;
        } else if (value === true || value === false) {
          updatedValue = value;
        } else if (value === null || typeof value === 'string') {
          updatedValue = value;
        }
      }

      const updated = { ...prev, [field]: updatedValue };

      if (formErrors[field]) {
        setFormErrors((prev) => {
          const { [field]: _, ...rest } = prev;
          return rest;
        });
      }

      return updated;
    });
  };

  const handleFieldError = (field, error, value) => {
    if (error) {
      setFormErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else if (formErrors[field]) {
      setFormErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    } else if (field.is_required && (value === null || value === undefined || value === '')) {
      return 'This field is required';
    }
  };

  const handleSaveChanges = async () => {
    const isCustomFieldsValid = customFieldsRef.current?.validateAllFields() ?? true; // Added validation call

    if (Object.keys(formErrors).length > 0 || !isCustomFieldsValid) {
      setSnackbar({
        open: true,
        message: 'Please fill the required fields before saving.',
        severity: 'error',
      });
      return; // Exit early, no save attempted
    }
    // First, check if there are any changes to save
    if (Object.keys(updatedFields).length === 0) {
      setSnackbar({
        open: true,
        message: 'No changes to save',
        severity: 'info',
      });
      setIsEditing(false); // Exit edit mode since there's nothing to save
      return; // Exit the function early
    }

    const nextStage = updatedFields.stage !== undefined ? updatedFields.stage : deal.stage;
    const nextValue = updatedFields.value !== undefined ? updatedFields.value : deal.value;
    if (nextStage === 'Closed Won') {
      const numericValue = Number(nextValue);
      if (!Number.isFinite(numericValue) || numericValue <= 0) {
        setSnackbar({
          open: true,
          message: 'Closed Won deals require a Value greater than 0.',
          severity: 'error',
        });
        return;
      }
    }

    // Legacy quotations only — travel tenants do not use quotation → order conversion.
    if (
      showLegacyQuotations &&
      updatedFields.stage === 'Closed Won' &&
      deal.stage !== 'Closed Won'
    ) {
      try {
        // Fetch quotations summary before showing modal
        const response = await axiosInstance.get(`/deals/${id}/quotations-summary`);
        setQuotationsSummary(response.data);

        // Show confirmation modal if there are quotations
        if (response.data.has_quotations) {
          setShowClosedWonModal(true);
          // Don't proceed with save yet - wait for user confirmation
          return;
        }
      } catch (error) {
        console.error('Error fetching quotations summary:', error);
      }
    }

    setIsDetailsLoading(true); // Show skeleton for the detail section only
    try {
      // Prepare the payload
      const payload = {
        ...deal, // Start with the original deal data
        ...updatedFields, // Override with updated fields
        updated_at: new Date().toISOString(),
        custom_fields: {}, // Add custom_fields object
      };

      // Explicitly set assigned_to and assigned_to_name from updatedFields if they exist
      if (updatedFields.assigned_to !== undefined) {
        payload.assigned_to = updatedFields.assigned_to;
      }
      if (updatedFields.assigned_to_name !== undefined) {
        payload.assigned_to_name = updatedFields.assigned_to_name;
      }

      // Move custom fields to custom_fields object
      Object.keys(payload).forEach(key => {
        if (key.startsWith('cf_')) {
          payload.custom_fields[key] = payload[key];
          delete payload[key];
        }
      });

      // Ensure all ID fields are strings
      if (payload.account_id) payload.account_id = String(payload.account_id);
      if (payload.contact_id) payload.contact_id = String(payload.contact_id);
      if (payload.lead_id) payload.lead_id = String(payload.lead_id);
      if (payload.assigned_to) payload.assigned_to = String(payload.assigned_to);

      console.log('Formatted payload:', payload);

      const response = await axiosInstance.put(`/deals/${deal.id}`, payload);

      // Update the deal state directly with the response data
      const updatedDeal = response.data;

      // Merge custom fields into the main deal data
      if (updatedDeal.custom_fields) {
        Object.entries(updatedDeal.custom_fields).forEach(([key, value]) => {
          updatedDeal[key] = value;
        });
      }

      setDeal(updatedDeal); // Update the deal state

      // Update the activeStep state if the stage has changed
      if (updatedFields.stage) {
        const newStep = dealStages.indexOf(updatedFields.stage);
        setActiveStep(newStep >= 0 ? newStep : 0);
      }

      setUpdatedFields({});
      setFormErrors({});
      setSnackbar({
        open: true,
        message: 'Deal updated successfully!',
        severity: 'success',
      });
      setIsEditing(false);

      // Log deal update
      await safeLog(() => frontendLogger.logUpdate('Deal', {
        id: deal.id,
        updated_fields: Object.keys(updatedFields),
        changes: updatedFields,
        action: 'edit',
        user: user.username,
        timestamp: new Date().toISOString(),
      }));

    } catch (error) {
      console.error('Error updating deal:', error);
      const errorMessage = error.response?.data?.detail?.[0]?.msg || 'Error updating deal. Please try again.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsDetailsLoading(false); // Hide skeleton after loading
    }
  };

  const handleCreateOrder = async () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to create orders.',
        severity: 'error',
      });
      return; // Exit the function early
    }
    if (!showLegacyOrders) {
      setSnackbar({
        open: true,
        message: 'Legacy orders are unavailable while Travel is enabled.',
        severity: 'error',
      });
      return;
    }
    setIsOrderLoading(true);
    try {
      // Fetch all quotations
      const quotationsResponse = await axiosInstance.get('/quotations/');

      // Filter quotations to find the ones for the current deal
      const dealQuotations = quotationsResponse.data.filter(
        (quotation) => quotation.deal_id === parseInt(id)
      );

      // Check if any quotations exist for this deal
      if (dealQuotations.length === 0) {
        setSnackbar({
          open: true,
          message: 'No quotations found for this deal.',
          severity: 'error',
        });
        setIsOrderLoading(false);
        return;
      }

      // Check if any quotations are Accepted
      const acceptedQuotation = dealQuotations.find(
        (quotation) => quotation.status === 'Accepted'
      );

      if (!acceptedQuotation) {
        setSnackbar({
          open: true,
          message: 'No Accepted quotation found for this deal.',
          severity: 'error',
        });
        setIsOrderLoading(false);
        return;
      }

      // Fetch quotation items
      console.log('Quotation id:', acceptedQuotation.id);
      const quotationItemsResponse = await axiosInstance.get(`/quotations/items/${acceptedQuotation.id}`);

      // Check inventory availability for all physical products
      const inventoryChecks = await Promise.all(
        quotationItemsResponse.data.map(async (item) => {
          const productId = item.product_id;

          // First, check product type
          const productResponse = await axiosInstance.get(`/products/${productId}`);
          const product = productResponse.data;

          // For non-physical products, ensure quantity is 1
          let quantity = item.quantity;
          if (product.product_type === 'Service' || product.product_type === 'Digital') {
            // if (quantity !== 1) {
            //   quantity = 1;
            //   console.log(`Set quantity to 1 for non-physical product: ${item.product_name}`);
            // }

            // Skip inventory check for non-physical products
            return {
              inventoryItem: null,
              orderItem: {
                ...item,
                quantity,
              },
              product,
              requiresInventory: false
            };
          }

          // Continue with inventory check for physical products
          const inventoryResponse = await axiosInstance.get(`/inventory/?product_id=${productId}`);
          const inventoryItems = inventoryResponse.data;
          const inventoryItem = inventoryItems.find(inv => inv.product_id === productId);

          if (!inventoryItem) {
            throw new Error(`Inventory not found for physical product ${item.product_name} (ID: ${productId})`);
          }

          if (inventoryItem.quantity < item.quantity) {
            throw new Error(
              `Insufficient inventory for product ${item.product_name}. ` +
              `Available: ${inventoryItem.quantity}, Ordered: ${item.quantity}`
            );
          }

          return {
            inventoryItem,
            orderItem: item,
            product,
            requiresInventory: true
          };
        })
      );

      const accountResponse = await axiosInstance.get(`/accounts/${deal.account_id}`);
      const accountData = accountResponse.data;

      // Format the full shipping and billing addresses
      const { shippingAddress, billingAddress } = combineAddress(accountData);

      const deliveryDate = new Date();
      deliveryDate.setMonth(deliveryDate.getMonth() + 1); // Add 1 month

      // Prepare order payload (order_number assigned by backend)
      const orderPayload = {
        deal_id: id,
        quotation_id: acceptedQuotation.id,
        total_amount: parseFloat(acceptedQuotation.total_amount || 0),
        grand_total: parseFloat(acceptedQuotation.grand_total || 0),
        subtotal: parseFloat(acceptedQuotation.subtotal || 0),
        status: 'Draft',
        order_date: new Date().toISOString(),
        created_by: user.id,
        updated_by: user.id,
        created_by_name: user.username,
        updated_by_name: user.username,
        deal_name: deal?.name || '', // Get deal name from current deal
        account_id: deal?.account_id || acceptedQuotation.account_id,
        account_name: accountData?.name || '', // Get account name from accountData
        contact_id: deal?.contact_id || acceptedQuotation.contact_id,
        contact_name: contactData ? `${contactData.first_name} ${contactData.last_name}` : '',
        shipping_method: acceptedQuotation.shipping_method,
        shipping_instruction: acceptedQuotation.notes,
        shipping_address: shippingAddress, // Use formatted shipping address
        billing_address: billingAddress, // Use formatted billing address
        delivery_date: deliveryDate.toISOString(), // Set delivery date to 1 month in the future
        currency: accountData?.currency || '',
      };

      console.log('orderPayload', orderPayload);
      // Create the order
      const orderResponse = await axiosInstance.post('/orders/', orderPayload);
      const newOrderId = orderResponse.data.id;

      // --- Log order creation ---
      await frontendLogger.logCreate('Order', {
        id: newOrderId,
        deal_id: id,
        action: 'create',
        created_by: user.id,
        user: user.username,
        created_at: orderResponse.data.created_at || new Date().toISOString(),
      });

      // Prepare order items payload with adjusted quantities for non-physical products
      const orderItemsPayload = inventoryChecks.map((check) => ({
        order_id: newOrderId,
        product_id: check.orderItem.product_id,
        product_name: check.orderItem.product_name,
        quantity: check.orderItem.quantity, // This may have been adjusted for non-physical products
        unit_price: parseFloat(check.orderItem.price),
        total_price: parseFloat(check.orderItem.total_price),
        tax: parseFloat(check.orderItem.tax),
        discounted_price: parseFloat(check.orderItem.discounted_price || 0),
        discount_percentage: parseFloat(check.orderItem.discount_percentage || 0),
      }));

      // Create order items
      await Promise.all(
        orderItemsPayload.map((item) =>
          axiosInstance.post('/orders/items/', item)
        )
      );

      // Update inventory quantities only for physical products
      const inventoryUpdates = inventoryChecks
        .filter(check => check.requiresInventory)
        .map(async (check) => {
          const { inventoryItem, orderItem } = check;
          const newQuantity = inventoryItem.quantity - orderItem.quantity;

          await axiosInstance.put(`/inventory/${inventoryItem.id}`, {
            ...inventoryItem,
            quantity: newQuantity,
            updated_by: user.id,
            updated_by_name: user.username,
          });

          console.log(`Updated inventory for physical product: ${orderItem.product_name} (-${orderItem.quantity} units)`);
        });

      await Promise.all(inventoryUpdates);

      // Log skipped inventory updates for non-physical products
      inventoryChecks
        .filter(check => !check.requiresInventory)
        .forEach(check => {
          console.log(`Skipping inventory update for non-physical product: ${check.orderItem.product_name}`);
        });

      // Update the deal stage to 'Closed Won'
      if (deal.stage !== 'Closed Won') {
        await axiosInstance.put(`/deals/${id}`, {
          stage: 'Closed Won',
          final_value: orderPayload.total_amount,
        });
      }

      // Show success message and navigate to order details
      setSnackbar({
        open: true,
        message: `Order ${orderResponse.data.order_number || newOrderId} and its items created successfully!`,
        severity: 'success',
      });
      navigate(`/orders/${newOrderId}`);

    } catch (error) {
      console.error('Error creating order:', error);

      // Detailed error handling
      let errorMessage = 'Failed to create order. Please try again.';

      // Check for inventory error message
      if (error.message && error.message.includes('Insufficient inventory')) {
        errorMessage = error.message; // Use the inventory error message directly
      } else if (error.response) {
        if (error.response.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data.detail) {
            if (Array.isArray(error.response.data.detail)) {
              errorMessage = error.response.data.detail
                .map((err) => `${err.loc[1] || 'Field'}: ${err.msg}`)
                .join('; ');
            } else {
              errorMessage = error.response.data.detail;
            }
          }
        } else if (error.response.status) {
          // Add status code if available
          errorMessage += ` (Status: ${error.response.status})`;
        }
      } else if (error.message) {
        // For other types of errors, use the error message
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
        autoHideDuration: 6000, // Show for 6 seconds
      });
    } finally {
      setIsOrderLoading(false);
    }
  };

  const handleOpenDeleteModal = () => setIsDeleteModalOpen(true);
  const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDelete = async () => {
    setIsLoading(true);
    handleCloseDeleteModal();
    try {
      await axiosInstance.delete(`/deals/${deal.id}`, {
        params: { delete_related: deleteRelated }
      });
      navigate('/deals');
    } catch (error) {
      console.error('Error deleting deal:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenActivityModal = () => {
    setIsActivityModalOpen(true);
  };

  const handleOpenQuotationModal = () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to create quotations.',
        severity: 'error',
      });
      return; // Exit the function early
    }
    setIsQuotationModalOpen(true);
  };
  const handleCloseQuotationModal = () => {
    setIsQuotationModalOpen(false);
    setNewQuotation({
      deal_id: id,
      total_amount: '',
    })
  }
  const handleCloseActivityModal = () => {
    setIsActivityModalOpen(false);
    setNewActivity({
      deal_id: id,
      type: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
    });
  };

  const handleActivitySaved = async (newActivityData) => {
    setIsActivitiesLoading(true);
    try {
      setActivities([...activities, newActivityData]);

      setSnackbar({
        open: true,
        message: 'Activity created successfully!',
        severity: 'success',
      });

      await fetchDealData(); // Refresh lead data to get updated information
    } catch (error) {
      console.error('Error updating last activity:', error);
      setSnackbar({
        open: true,
        message: 'Error updating last activity',
        severity: 'error',
      });
    } finally {
      setIsActivitiesLoading(false);
    }
  };

  const isFinalStage = (stage) => {
    return stage === 'Closed Won' || stage === 'Closed Lost';
  };

  // Helper function to check if movement is allowed
  const isStageMovementAllowed = (currentStage, targetStage) => {
    // If current stage is final, don't allow any movement
    if (isFinalStage(currentStage)) {
      return false;
    }

    // Allow movement to any stage if not in a final stage
    return true;
  };

  const handleStageClick = async (index) => {
    if (isEditing) return false;

    const newStage = dealStages[index];
    const currentStage = deal.stage;

    if (newStage === currentStage) return false;

    if (!isStageMovementAllowed(currentStage, newStage)) {
      setSnackbar({
        open: true,
        message: `Cannot move deal from ${currentStage} stage as it is a final stage.`,
        severity: 'warning',
      });
      return false;
    }

    setIsDetailsLoading(true);
    try {
      const payload = {
        stage: newStage,
        closed_lost_comment: newStage === 'Closed Lost' ? closedLostComment : null,
      };

      const response = await axiosInstance.put(`/deals/${deal.id}`, payload);
      setDeal(response.data);
      setActiveStep(index);

      await safeLog(() => frontendLogger.logUpdate('Deal', {
        id: deal.id,
        action: 'stepper_change',
        old_stage: currentStage,
        new_stage: newStage,
        user: user.username,
        timestamp: new Date().toISOString(),
      }));

      setSnackbar({
        open: true,
        message: `Deal stage updated to ${newStage}`,
        severity: 'success',
      });

      setClosedLostComment('');
      fetchDealData();
      return true;
    } catch (error) {
      console.error('Error updating deal stage:', error);
      setSnackbar({
        open: true,
        message: 'Error updating deal stage. Please try again.',
        severity: 'error',
      });

      const currentStep = dealStages.indexOf(deal.stage);
      setActiveStep(currentStep >= 0 ? currentStep : 0);
      return false;
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axiosInstance.get('/users/');
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const handleAgentClick = (agentId) => {
    if (agentId) {
      navigate(`/users/${agentId}/profile`);
    } else {
      setSnackbar({
        open: true,
        message: 'This deal is not assigned to any agentv.',
        severity: 'info',
      });
    }
  };

  const handleContactAgentClick = (agentId) => {
    if (agentId) {
      navigate(`/contacts/${agentId}`);
    } else {
      setSnackbar({
        open: true,
        message: 'This deal is not assigned to any agent.',
        severity: 'info',
      });
    }
  };

  const handleAccountAgentClick = (agentId) => {
    if (agentId) {
      navigate(`/accounts/${agentId}`);
    } else {
      setSnackbar({
        open: true,
        message: 'This deal is not assigned to any agent.',
        severity: 'info',
      });
    }
  };

  const fetchAttachments = async () => {
    const parentTable = 'deals';
    const parentId = id;
    try {
      const response = await axiosInstance.get('/attachments/', {
        params: {
          parent_table: parentTable,
          parent_id: parentId
          // skip: 0,
          // limit: 100,
          // sort_by: 'created_at',
          // sort_order: 'desc'
        }
      });

      setAttachments(response.data || []);

    } catch (error) {
      console.error('Error fetching attachments:', error);
      const errorMessage = error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Error fetching attachments';

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
        autoHideDuration: 5000
      });
    } finally {
      // If you have a loading state
      // setLoading(false);
    }
  };


  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axiosInstance.delete(`/attachments/${attachmentId}`);
      setAttachments(attachments.filter((attachment) => attachment.id !== attachmentId));
      setSnackbar({
        open: true,
        message: 'Attachment deleted successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting attachment',
        severity: 'error',
      });
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      if (!attachment.bucket_name || !attachment.storage_path) {
        throw new Error('Attachment storage metadata is invalid for strict R2 download');
      }
      await downloadPrivateObject({
        axiosInstance,
        attachmentId: attachment.id,
        fileName: attachment.file_name
      });
    } catch (error) {
      console.error('Error downloading attachment:', error);
      setSnackbar({
        open: true,
        message: 'Error downloading attachment',
        severity: 'error',
      });
    }
  };

  const handleUploadSuccess = async (uploadData) => {
    // console.log('Upload successful:', uploadData);

    const uploadDataPayload = {
      file_name: uploadData.file.name,
      file_type: uploadData.file.type,
      file_size: uploadData.file.size,
      bucket_name: uploadData.bucket,
      storage_path: uploadData.path,
      description: uploadData.description || '', // Optional, use default if no description is provided
      parent_table: 'deals',  // Assuming the parent table is 'deals', modify as needed
      parent_id: id,  // Assuming the parent ID is the same as lead ID
      is_active: true,
      is_private: uploadData.isPrivate || false, // Default to false if not provided
      file_url: uploadData.url,
      signed_url_expiry: uploadData.signedUrlExpiry || null,
    };

    setIsAttachmentModalOpen(false);
    try {
      const response = await axiosInstance.post(`/attachments/`, uploadDataPayload);

      setSnackbar({
        open: true,
        message: 'File uploaded successfully!',
        severity: 'success',
      });

      // Update files or attachments state as needed
      // setFiles([...files, response.data]); // Uncomment if `files` state exists
    } catch (error) {
      console.error('Error uploading file:', error);
      setSnackbar({
        open: true,
        message: 'Error uploading file',
        severity: 'error',
      });
    } finally {
      fetchAttachments();
    }
  };

  const handleDeleteActivity = async (activityId) => {
    setIsLoading(true);
    try {
      await axiosInstance.delete(`/activities/${activityId}`);
      setActivities(activities.filter((activity) => activity.id !== activityId));
      setSnackbar({
        open: true,
        message: 'Activity deleted successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting activity:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting activity',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditActivity = (updated) => {
    if (!updated?.id) return;
    setActivities((prev) =>
      prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
    );
  };

  const fetchAccountData = async (accountId) => {
    if (!accountId) return;
    try {
      const response = await axiosInstance.get(`/accounts/${accountId}`);
      const accountData = response.data;

      // Format the full shipping and billing addresses using combineAddress
      const { shippingAddress, billingAddress } = combineAddress(accountData);
      accountData.shipping_address = shippingAddress;
      accountData.billing_address = billingAddress;

      setAccountData(accountData);
    } catch (error) {
      console.error('Error fetching account details:', error);
      setSnackbar({
        open: true,
        message: 'You are not the Account Owner for this Deal.',
        severity: 'error',
      });
    }
  };

  // Helper function to combine address parts into a full address string
  const combineAddress = (account) => {
    const shippingParts = [
      account?.shipping_address || '',
      account?.shipping_city || '',
      account?.shipping_postal_code || '',
      account?.shipping_state || '',
      account?.shipping_country || '',
    ].filter(part => part).join(', ');

    const billingParts = [
      account?.billing_address || '',
      account?.billing_city || '',
      account?.billing_postal_code || '',
      account?.billing_state || '',
      account?.billing_country || '',
    ].filter(part => part).join(', ');

    return { shippingAddress: shippingParts || '', billingAddress: billingParts || '' };
  };

  const fetchContactData = async (contactId) => {
    if (!contactId) return;
    try {
      const response = await axiosInstance.get(`/contacts/${contactId}`);
      setContactData(response.data);
    } catch (error) {
      console.error('Error fetching contact details:', error);
      setSnackbar({
        open: true,
        message: 'You are not the Contact Owner for this Deal.',
        severity: 'error',
      });
    }
  };

  const fetchLeadData = async (leadId) => {
    if (!leadId) return;
    try {
      const response = await axiosInstance.get(`/leads/${leadId}`);
      setLeadData(response.data);
    } catch (error) {
      console.error('Error fetching lead details:', error);
      setSnackbar({
        open: true,
        message: 'You are not the Lead Owner for this Deal.',
        severity: 'error',
      });
    }
  };


  const SuccessAnimation = ({ isOpen, onClose }) => {
    useEffect(() => {
      if (isOpen) {
        // Close the success animation after 3 seconds
        const timer = setTimeout(() => {
          onClose();
        }, 3000);

        // Clean up the timer if the component is unmounted or isOpen is set to false
        return () => clearTimeout(timer);
      }
    }, [isOpen, onClose]);

    return (
      <AnimatePresence>
        {isOpen && (
          <div
            className="fixed inset-0 flex items-center justify-center z-50 bg-black/50"
            onClick={onClose} // Close on background click
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-lg p-8 shadow-lg w-96 text-center relative"
            >
              {/* Success Text */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <MDTypography
                  variant="h3"
                  fontWeight="bold"
                  color="text.primary" // Change to a visible color
                  textTransform="none"
                  mb={2}
                >
                  Success!
                </MDTypography>
              </motion.div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <MDTypography
                  variant="body2"
                  color="text.secondary" // Change to a visible color
                  mb={4}
                >
                  You've successfully closed the deal. Keep up the great work!
                </MDTypography>
              </motion.div>

              {/* Confetti Animation */}
              <Confetti
                width={window.innerWidth}
                height={window.innerHeight}
                recycle={false}
                numberOfPieces={300}
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    );
  };


  // In DealDetail.js, modify the handleSaveQuotation function

  const handleSaveQuotation = async (data) => {
    console.log('Received data from modal:', data);
    setIsLoading(true);

    try {
      // Validate user data
      if (!user?.id || !user?.username) {
        throw new Error('User data is missing. Please ensure you are logged in.');
      }

      // Validate items first
      if (!data.items || data.items.length === 0) {
        throw new Error('Cannot create quotation without items');
      }

      // Validate each item
      data.items.forEach((item, index) => {
        if (!item.product_name || !item.quantity || !item.price) {
          throw new Error(`Invalid data in item ${index + 1}. All items must have a product, quantity, and price.`);
        }
      });

      // Extract custom fields (any field starting with cf_)
      const customFields = {};
      Object.keys(data).forEach(key => {
        if (key.startsWith('cf')) {
          customFields[key] = data[key];
        }
      });

      // Create quotation payload
      const quotationData = {
        deal_id: id,
        quote_name: data.quote_name,
        total_amount: Number(data.total_amount) || 0,
        grand_total: Number(data.total_amount) || 0,
        status: data.status || 'Draft',
        notes: data.notes || '',
        shipping_method: data.shipping_method || '',
        payment_methods: data.payment_methods || '',
        tax: data.tax,
        quote_date: data.quote_date || null,
        delivery_date: data.delivery_date || null,
        subtotal: Number(data.subtotal) || 0,
        created_by: user.id,
        updated_by: user.id,
        created_by_name: user.username,
        updated_by_name: user.username,
        unit_price: Number(data.unit_price) || 0,
        // Tax slabs
        tax_slab_1: data.tax_slab_1 || null,
        tax_slab_2: data.tax_slab_2 || null,
        tax_slab_3: data.tax_slab_3 || null,
        tax_slab_4: data.tax_slab_4 || null,
        tax_slab_5: data.tax_slab_5 || null,
        tax_slab_amount_1: data.tax_slab_amount_1 || null,
        tax_slab_amount_2: data.tax_slab_amount_2 || null,
        tax_slab_amount_3: data.tax_slab_amount_3 || null,
        tax_slab_amount_4: data.tax_slab_amount_4 || null,
        tax_slab_amount_5: data.tax_slab_amount_5 || null,
        currency: data.currency || 'INR',
        // Add custom fields directly in the root
        ...customFields,
        // Also include in custom_fields object for API compatibility
        custom_fields: customFields
      };

      console.log('Creating quotation with custom fields:', quotationData);

      // Create the quotation first
      const quotationResponse = await axiosInstance.post('/quotations/', quotationData);

      if (!quotationResponse.data || !quotationResponse.data.id) {
        throw new Error('Invalid response from server: Missing quotation ID');
      }

      const newQuotationId = quotationResponse.data.id;

      // --- Log quotation creation ---
      await frontendLogger.logCreate('Quotation', {
        id: newQuotationId,
        deal_id: id,
        action: 'create',
        created_by: user.id,
        user: user.username,
        created_at: quotationResponse.data.created_at || new Date().toISOString(),
      });

      // Step 2: Prepare and send items separately with all required fields
      if (data.items && data.items.length > 0) {
        const quotationItems = data.items.map(item => ({
          quotation_id: newQuotationId,
          product_id: item.product_id,
          product_name: item.product_name || item.name || '',
          quantity: Number(item.quantity) || 0,
          total_price: Number(item.total_price) || 0,
          description: item.description || '',
          price: Number(item.price) || 0,
          Unit_price: Number(item.price) || 0,
          discount_percentage: Number(item.discount_percentage) || 0,
          discounted_price: Number(item.discounted_price) || 0,
          tax: item.tax
          // Remove all the duplicate fields and tax slabs from here
        }));

        console.log('Creating quotation items:', quotationItems);

        // Add validation before sending
        if (quotationItems.some(item => !item.product_name)) {
          throw new Error('Product name is required for all items');
        }

        // Send items to the correct endpoint
        await axiosInstance.post(`/quotations/items/${newQuotationId}`, quotationItems)
          .catch(error => {
            console.error('Items creation error response:', error.response?.data);
            throw new Error(
              error.response?.data?.detail?.[0]?.msg ||
              'Failed to create quotation items'
            );
          });
      }

      // Step 3: Fetch the complete quotation with items
      const updatedQuotationResponse = await axiosInstance.get(`/quotations/${newQuotationId}`);

      setQuotations(prev => [...prev, updatedQuotationResponse.data]);

      setSnackbar({
        open: true,
        message: 'Quotation created successfully!',
        severity: 'success',
      });

      handleCloseQuotationModal();

    } catch (error) {
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        details: error.response?.data?.detail
      });

      // More user-friendly error message
      const errorMessage = error.response?.data?.detail?.[0]?.msg ||
        error.message ||
        'Failed to save quotation';

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };



  const handleStageChange = async (index) => {
    const targetStage = dealStages[index];
    if (targetStage === deal?.stage) return;

    if (targetStage === 'Closed Lost') {
      // console.log('Triggering Closed Lost Modal');
      setShowClosedLostModal(true);
      setPendingClosedLostChange(index);
      return;
    }

    if (targetStage === 'Closed Won') {
      const numericValue = Number(deal?.value);
      if (!Number.isFinite(numericValue) || numericValue <= 0) {
        setSnackbar({
          open: true,
          message: 'Closed Won deals require a Value greater than 0. Update Value before closing.',
          severity: 'error',
        });
        return;
      }

      // Travel UI replaces legacy quotations; skip quotation conversion prompt.
      if (!showLegacyQuotations) {
        await handleStageClick(index);
        return;
      }

      try {
        // Fetch quotations summary before showing modal
        const response = await axiosInstance.get(`/deals/${id}/quotations-summary`);
        setQuotationsSummary(response.data);

        // Show confirmation modal if there are quotations
        if (response.data.has_quotations) {
          setShowClosedWonModal(true);
          setPendingClosedWonChange(index);
          return;
        }

        // If no quotations, proceed directly
        await handleStageClick(index);


      } catch (error) {
        console.error('Error updating stage:', error);
        setSnackbar({
          open: true,
          message: 'Error updating deal stage',
          severity: 'error',
        });
      }
      return;
    }

    setStageStepConfirm({ open: true, index });
  };

  const handleStageStepConfirmCancel = () => {
    setStageStepConfirm({ open: false, index: null });
  };

  const handleStageStepConfirm = async () => {
    const index = stageStepConfirm.index;
    if (index == null) {
      handleStageStepConfirmCancel();
      return;
    }
    setStageStepConfirm({ open: false, index: null });
    await handleStageClick(index);
  };



  const handleClosedLostSubmit = async () => {
    if (!closedLostComment) {
      setSnackbar({
        open: true,
        message: 'Please provide a comment for closing the deal as Lost.',
        severity: 'warning',
      });
      return;
    }

    // Set the pending state to show the process is ongoing
    setPendingClosedLostChange(deal.id);

    try {
      setIsLoading(true);
      const updatedDeal = {
        ...deal,
        stage: 'Closed Lost', // Updating the deal stage to 'Closed Lost'
        closed_lost_comment: closedLostComment, // Adding the closed lost comment
      };

      // Update the deal status in the backend
      const response = await axiosInstance.put(`/deals/${deal.id}`, updatedDeal);
      setDeal(response.data);

      // Show success message
      setSnackbar({
        open: true,
        message: 'Deal marked as Closed Lost successfully!',
        severity: 'success',
      });

      // Close the modal and reset the comment
      setShowClosedLostModal(false);
      setClosedLostComment('');

      // Refresh the parent section or card
      refreshSection(); // Call a function to refresh data and re-render the UI

    } catch (error) {
      console.error('Error closing deal as Lost:', error);
      setSnackbar({
        open: true,
        message: 'Failed to mark deal as Closed Lost. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
      setPendingClosedLostChange(null); // Reset pending state
    }
  };

  const handleClosedWonSubmit = async () => {
    const nextValue = updatedFields.value !== undefined ? updatedFields.value : deal?.value;
    const numericValue = Number(nextValue);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      setSnackbar({
        open: true,
        message: 'Closed Won deals require a Value greater than 0. Update Value before closing.',
        severity: 'error',
      });
      return;
    }
    setIsClosedWonSaving(true);
    try {
      setIsDetailsLoading(true);

      // Check if called from stepper (has pendingClosedWonChange) or from edit mode (has updatedFields)
      if (pendingClosedWonChange !== null) {
        const ok = await handleStageClick(pendingClosedWonChange);
        if (!ok) return;   // handleStageClick already showed the error snackbar

        // Refresh quotations list
        await fetchQuotations();

        setSnackbar({
          open: true,
          message: 'Deal marked as Closed Won successfully! Quotations updated.',
          severity: 'success',
        });
      } else {
        // Called from edit mode - need to save the changes
        const payload = {
          ...deal, // Start with the original deal data
          ...updatedFields, // Override with updated fields
          updated_at: new Date().toISOString(),
          custom_fields: {}, // Add custom_fields object
        };

        // Explicitly set assigned_to and assigned_to_name from updatedFields if they exist
        if (updatedFields.assigned_to !== undefined) {
          payload.assigned_to = updatedFields.assigned_to;
        }
        if (updatedFields.assigned_to_name !== undefined) {
          payload.assigned_to_name = updatedFields.assigned_to_name;
        }

        // Move custom fields to custom_fields object
        Object.keys(payload).forEach(key => {
          if (key.startsWith('cf_')) {
            payload.custom_fields[key] = payload[key];
            delete payload[key];
          }
        });

        // Ensure all ID fields are strings
        if (payload.account_id) payload.account_id = String(payload.account_id);
        if (payload.contact_id) payload.contact_id = String(payload.contact_id);
        if (payload.lead_id) payload.lead_id = String(payload.lead_id);
        if (payload.assigned_to) payload.assigned_to = String(payload.assigned_to);

        const response = await axiosInstance.put(`/deals/${deal.id}`, payload);

        // Update the deal state directly with the response data
        const updatedDeal = response.data;

        // Merge custom fields into the main deal data
        if (updatedDeal.custom_fields) {
          Object.entries(updatedDeal.custom_fields).forEach(([key, value]) => {
            updatedDeal[key] = value;
          });
        }

        setDeal(updatedDeal); // Update the deal state

        // Update the activeStep state if the stage has changed
        if (updatedFields.stage) {
          const newStep = dealStages.indexOf(updatedFields.stage);
          setActiveStep(newStep >= 0 ? newStep : 0);
        }

        // Refresh quotations list
        await fetchQuotations();

        setUpdatedFields({});
        setFormErrors({});
        setSnackbar({
          open: true,
          message: 'Deal updated successfully! Quotations updated.',
          severity: 'success',
        });
        setIsEditing(false);

        // Log deal update
        await safeLog(() => frontendLogger.logUpdate('Deal', {
          id: deal.id,
          updated_fields: Object.keys(updatedFields),
          changes: updatedFields,
          action: 'edit',
          user: user.username,
          timestamp: new Date().toISOString(),
        }));
      }

      // Close the modal
      setShowClosedWonModal(false);
      setPendingClosedWonChange(null);



    } catch (error) {
      console.error('Error closing deal as Won:', error);
      setSnackbar({
        open: true,
        message: 'Failed to mark deal as Closed Won. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsClosedWonSaving(false);
      setIsDetailsLoading(false);
    }
  };



  const refreshSection = () => {
    // Fetch updated data or trigger a re-render
    fetchDealData(); // Replace with your data-fetching logic
  };


  const FinalStageWarningModal = () => (
    <Modal
      open={showFinalStageWarning}
      onClose={() => setShowFinalStageWarning(false)}
      aria-labelledby="final-stage-warning-modal"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
      }}>
        <MDTypography id="final-stage-warning-modal" variant="h6" component="h2" mb={2}>
          Warning: Final Stage
        </MDTypography>
        <MDTypography variant="body1" mb={3}>
          Moving to {pendingStageChange !== null ? dealStages[pendingStageChange] : ''} is a final action and cannot be undone. Are you sure you want to proceed?
        </MDTypography>
        <MDBox display="flex" justifyContent="flex-end" gap={2}>
          <MDButton onClick={() => setShowFinalStageWarning(false)} color="secondary">
            Cancel
          </MDButton>
          <MDButton
            onClick={() => {
              setShowFinalStageWarning(false);
              handleStageClick(pendingStageChange);
            }}
            variant="gradient"
            color="primary"
          >
            Proceed
          </MDButton>
        </MDBox>
      </Box>
    </Modal>
  );


  // Toggle functions for CC and BCC fields
  const toggleCc = () => setShowCc(!showCc);
  const toggleBcc = () => setShowBcc(!showBcc);

  // Function to handle Quill editor changes
  const handleQuillChange = (content) => {
    setNewEmail({ ...newEmail, content });
  };

  // Define Quill editor modules and formats
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'link', 'image',
  ];


  const fetchQuotationPdfBlob = async (quotationId) => {
    const response = await axiosInstance.get(`/quotations/pdf/${quotationId}`, {
      responseType: 'blob',
      timeout: 30000,
    });

    if (response.data?.type && response.data.type !== 'application/pdf') {
      throw new Error('Invalid response format - not a PDF');
    }

    return new Blob([response.data], { type: 'application/pdf' });
  };

  const handleToggleEmailModal = async (quotationOrEvent) => {
    try {
      setIsGeneratingEmail(true);

      // Header Send Email passes a click event; quotation cards pass a quotation object.
      const quotation =
        quotationOrEvent &&
        typeof quotationOrEvent === 'object' &&
        !quotationOrEvent.nativeEvent &&
        quotationOrEvent.id != null &&
        (quotationOrEvent.quote_name != null || quotationOrEvent.deal_id != null)
          ? quotationOrEvent
          : null;

      setSelectedQuotation(quotation);
      setEmailAttachments([]);

      const recipientEmail = accountData?.email || contactData?.email || '';

      // Prepare initial email data
      const emailData = {
        to: recipientEmail,
        cc: '',
        bcc: '',
        subject: '',
        content: '',
      };

      // If quotation is provided, prefill and attach PDF
      if (quotation) {
        const quoteName = quotation.quote_name || `Quote-${quotation.id}`;
        emailData.subject = `Quotation ${quoteName}`;
        emailData.content = `Dear Sir/Madam,<br/><br/>Please find attached Quotation <strong>${quoteName}</strong>.<br/><br/>Please review and let us know if you have any questions.<br/><br/>Best regards`;

        try {
          const pdfBlob = await fetchQuotationPdfBlob(quotation.id);
          const maxSize = 25 * 1024 * 1024;
          if (pdfBlob.size > maxSize) {
            throw new Error('PDF file exceeds 25MB limit');
          }

          setEmailAttachments([{
            filename: `quotation_${quotation.id}.pdf`,
            size: pdfBlob.size,
            content_type: 'application/pdf',
            blob: pdfBlob,
          }]);
        } catch (pdfError) {
          console.error('Error attaching quotation PDF:', pdfError);
          setSnackbar({
            open: true,
            message: pdfError.message || 'Failed to attach quotation PDF',
            severity: 'warning',
          });
        }
      } else {
        const dealName = deal?.name || 'Deal';
        // Default email content based on deal stage
        switch (deal?.stage) {
          case 'Qualification':
            emailData.subject = `Initial Discussion: ${dealName} - Follow Up`;
            emailData.content = `Dear Sir/Madam,<br/><br/>Following up on our discussion regarding <strong>${dealName}</strong>.<br/><br/>Best regards`;
            break;

          case 'Needs Analysis':
            emailData.subject = `Needs Analysis: ${dealName}`;
            emailData.content = `Dear Sir/Madam,<br/><br/>Following up on the needs analysis for <strong>${dealName}</strong>.<br/><br/>Best regards`;
            break;

          case 'Proposal':
            emailData.subject = `Proposal for ${dealName}`;
            emailData.content = `Dear Sir/Madam,<br/><br/>Please find details related to our proposal for <strong>${dealName}</strong>.<br/><br/>Best regards`;
            break;

          case 'Negotiation':
            emailData.subject = `Negotiation Update: ${dealName}`;
            emailData.content = `Dear Sir/Madam,<br/><br/>Regarding ongoing negotiation for <strong>${dealName}</strong>.<br/><br/>Best regards`;
            break;

          case 'Closed Won':
            emailData.subject = `Deal Closed Won: ${dealName}`;
            emailData.content = `Dear Sir/Madam,<br/><br/>We are pleased to confirm that <strong>${dealName}</strong> has been closed successfully.<br/><br/>Best regards`;
            break;

          case 'Closed Lost':
            emailData.subject = `Regarding ${dealName}`;
            emailData.content = `Dear Sir/Madam,<br/><br/>Following up regarding <strong>${dealName}</strong>.<br/><br/>Best regards`;
            break;

          default:
            emailData.subject = `Regarding ${dealName}`;
            emailData.content = `Dear Sir/Madam,<br/><br/>Regarding <strong>${dealName}</strong>.<br/><br/>Best regards`;
        }
      }

      setNewEmail(emailData);
      setEmailModalOpen(true);
    } catch (error) {
      console.error('Error preparing email:', error);
      setSnackbar({
        open: true,
        message: 'Error preparing email content',
        severity: 'error',
      });
    } finally {
      setIsGeneratingEmail(false);
    }
  };


  // Add this helper function near other utility functions
  const stripHtmlTags = (html) => {
    if (!html) return '';
    // First replace <br>, <p>, and other block elements with newlines
    const textWithNewlines = html
      .replace(/<\/(p|div|br|h[1-6])>/gi, '\n')
      .replace(/<br\/?>/gi, '\n');
    // Then remove all HTML tags and decode HTML entities
    const div = document.createElement('div');
    div.innerHTML = textWithNewlines;
    return div.textContent || div.innerText || '';
  };

  // Update the handleSendEmail function
  const handleSendEmail = async () => {
    setIsEmailSending(true); // Start loading
    try {
      if (!newEmail.to || !newEmail.subject || !newEmail.content) {
        const missingFields = [];
        if (!newEmail.to) missingFields.push('To');
        if (!newEmail.subject) missingFields.push('Subject');
        if (!newEmail.content) missingFields.push('Content');
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const attachments = await serializeEmailAttachments(emailAttachments);

      const emailPayload = {
        to: newEmail.to,
        subject: newEmail.subject,
        content: newEmail.content,
        cc: newEmail.cc || '',
        bcc: newEmail.bcc || '',
        parent_table: 'deals',
        parent_table_id: id,
        attachments: attachments.length > 0 ? attachments : undefined,
      };

      // Send email
      const emailResponse = await axiosInstance.post('/emails/send', emailPayload);

      // After email is sent successfully, create an activity with local wall-clock
      // due_date (same as Lead Add Activity) — avoid toISOString() UTC shift.
      const now = new Date();
      const cleanDescription = stripHtmlTags(newEmail.content);

      const activityPayload = {
        title: `Email: ${newEmail.subject}`,
        type: "Email",
        status: "Completed",
        description: cleanDescription,
        starttime: null,
        endtime: null,
        due_date: toLocalDateTimeApi(now),
        parent_table: "deals",
        parent_table_id: id,
        assigned_to: user.id,
        assigned_to_name: user.fullname,
        all_day: false,
        color: "#edc949"
      };

      // Create activity
      const activityResponse = await axiosInstance.post('/activities/', activityPayload);

      if (!activityResponse.data) {
        throw new Error('Failed to create activity record');
      }

      setSnackbar({
        open: true,
        message: 'Email sent and activity created successfully',
        severity: 'success',
      });

      // Clear email data and close modal
      setEmailAttachments([]);
      setSelectedQuotation(null);
      setNewEmail({
        to: '',
        cc: '',
        bcc: '',
        subject: '',
        content: ''
      });
      setEmailModalOpen(false); // Close the modal

      // Refresh activities list
      await fetchActivities();

    } catch (error) {
      console.error('Error sending email or creating activity:', error);
      let errorMessage = 'Error sending email';

      if (error.response) {
        const { status, data } = error.response;
        errorMessage = typeof data.detail === 'string'
          ? data.detail
          : Array.isArray(data.detail)
            ? data.detail[0]?.msg || 'Unknown error'
            : 'Server error';

        if (status === 404 && data.detail === 'Email settings not found') {
          errorMessage = 'Email settings not configured. Please set up your email provider in settings.';
        }
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsEmailSending(false); // Stop loading
    }
  };


  const renderStepper = () => {
    const isInFinalStage = isFinalStage(deal?.stage);

    return (
      <MDBox>

        {/* Custom Stepper */}
        <ThemedCustomStepper
          activeStep={activeStep}
          steps={dealStages}
          onStepClick={
            !isLoading && !isEditing && !isInFinalStage ? handleStageChange : undefined
          }
          disabled={isLoading || isEditing || isInFinalStage}
          completedSteps={dealStages
            .slice(0, activeStep + 1)
            .map((_, index) => index)}
          stepProps={dealStages.map((stage, index) => ({
            completed: index <= activeStep,
            disabled:
              isLoading || isEditing || (isInFinalStage && index !== activeStep),
            sx: {
              '& .MuiStepLabel-label': {
                color:
                  isInFinalStage && index === activeStep
                    ? 'primary.main'
                    : 'inherit',
                fontWeight:
                  isInFinalStage && index === activeStep ? 'bold' : 'inherit',
              },
            },
          }))}
        />

        {/* Modal for Success Animation */}
        <Modal
          open={showSuccessAnimation}
          onClose={() => setShowSuccessAnimation(false)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.5)', // Dim background
          }}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              textAlign: 'center',
            }}
          >
            {/* Success Animation Component */}
            <SuccessAnimation
              isOpen={showSuccessAnimation}
              onClose={() => setShowSuccessAnimation(false)}
            />
          </Box>
        </Modal>
      </MDBox>
    );
  };

  const renderSection = (title, fields, renderContent = null) => {
    // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
    const sectionBody = (
      <>
      <Grid container spacing={3}>
        {fields
          .filter((item) => {
            if (item.field === 'closed_lost_comment') {
              return deal?.stage === 'Closed Lost';
            }
            if (item.hasOwnProperty('show')) {
              return item.show;
            }
            return true;
          })
          .map((item) => {
            let displayValue = item.dummyValue || deal?.[item.field];
            if (item.field === 'created_at' || item.field === 'updated_at') {
              displayValue = formatDate(displayValue, 'datetime');
            } else if (typeof displayValue === 'string' && item.field !== 'email') {
              displayValue = capitalizeFirstLetter(displayValue);
            }
            return (
              <Grid item xs={12} md={6} key={item.field}>
                {item.field === 'contact_id' || item.field === 'account_id' || item.field === 'assigned_to_name' ? (
                  <InlineEditField
                    label={item.label}
                    value={
                      item.field === 'assigned_to_name'
                        ? deal?.assigned_to || '' // Use assigned_to (ID) as the value
                        : deal?.[item.field] || leadData?.[item.field] || ''
                    }
                    type="dropdown"
                    options={
                      item.field === 'contact_id'
                        ? loadingAccountContacts ? [] : accountContacts.map(contact => ({
                          value: contact.id,
                          label: contact.first_name ? `${capitalizeFirstLetter(contact.first_name)} ${capitalizeFirstLetter(contact.last_name)}` : capitalizeFirstLetter(contact.last_name)
                        }))
                        : item.field === 'account_id'
                          ? accountagents.map(account => ({
                            value: account.id,
                            label: capitalizeFirstLetter(account.name),
                          }))
                          : item.field === 'assigned_to_name'
                            ? agents.map(user => ({
                              value: String(user.id),
                              label: capitalizeFirstLetter(user.fullname || user.username),
                            }))
                            : item.options ?? []
                    }
                    onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                    isEditing={isEditing}
                    readOnly={item.readOnly ?? false}
                    renderValue={
                      item.field === 'contact_id'
                        ? (value) => (
                          <MDTypography
                            component="span"
                            onClick={() => handleContactAgentClick(value)}
                            variant="button"
                            color="info"
                            fontWeight="medium"
                            sx={{
                              cursor: 'pointer'
                            }}
                          >
                            {(() => {
                              const contact = contactagents.find(contact => contact.id === value);
                              return contact ?
                                (contact.first_name ?
                                  `${capitalizeFirstLetter(contact.first_name)} ${capitalizeFirstLetter(contact.last_name)}` :
                                  capitalizeFirstLetter(contact.last_name)) || ' ' :
                                ' ';
                            })()}
                          </MDTypography>
                        )
                        : item.field === 'account_id'
                          ? (value) => (
                            <MDTypography
                              component="span"
                              onClick={() => handleAccountAgentClick(value)}
                              variant="button"
                              color="info"
                              fontWeight="medium"
                              sx={{
                                cursor: 'pointer'
                              }}
                            >
                              {capitalizeFirstLetter(accountagents.find(account => account.id === value)?.name || ' ')}
                            </MDTypography>
                          )
                          : item.field === 'assigned_to_name'
                            ? (value) => {
                              const assignedAgent = agents.find(agent => String(agent.id) === String(value));
                              return (
                                <MDTypography
                                  component="span"
                                  onClick={() => handleAgentClick(value)}
                                  variant="button"
                                  color="info"
                                  fontWeight="medium"
                                  sx={{
                                    cursor: 'pointer'
                                  }}
                                >
                                  {assignedAgent
                                      ? capitalizeFirstLetter(assignedAgent.fullname || assignedAgent.username)
                                      : capitalizeFirstLetter(deal?.assigned_to_name || ' ')}
                                </MDTypography>
                              );
                            }
                            : undefined
                    }
                  />
                ) : item.field === 'lead_id' ? (
                  isEditing ? (
                    <InlineEditField
                      label={item.label || 'Lead:'}
                      value={
                        leadData?.first_name
                          ? `${capitalizeFirstLetter(leadData.first_name)}${leadData.last_name ? ' ' + capitalizeFirstLetter(leadData.last_name) : ''}`
                          : (leadData?.last_name ? capitalizeFirstLetter(leadData.last_name) : '')
                      }
                      isEditing={false}
                      readOnly={true}
                    />
                  ) : (
                    <MDBox display="flex" alignItems="center">
                      <MDTypography variant="button" color="text" mr={1} fontWeight="medium">
                        {item.label || 'Lead:'}
                      </MDTypography>
                      <MDTypography
                        component="span"
                        onClick={() => deal?.lead_id && navigate(`/leads/${deal.lead_id}`)}
                        variant="button"
                        color="info"
                        fontWeight="medium"
                        sx={{
                          cursor: deal?.lead_id ? 'pointer' : 'default',
                          // textDecoration: deal?.lead_id ? 'underline' : 'none',
                        }}
                      >
                        {leadData?.first_name
                          ? `${capitalizeFirstLetter(leadData.first_name)}${leadData.last_name ? ' ' + capitalizeFirstLetter(leadData.last_name) : ''}`
                          : (leadData?.last_name ? capitalizeFirstLetter(leadData.last_name) : 'View Lead')}
                      </MDTypography>
                    </MDBox>
                  )
                ) : (
                  <InlineEditField
                    label={item.label}
                    value={
                      item.field === 'estimated_closure_date'
                        ? (deal?.estimated_closure_date ? formatDateNoTime(deal.estimated_closure_date) : '')
                        : (item.field === 'email' ? displayValue : (typeof displayValue === 'string' ? capitalizeFirstLetter(displayValue) : displayValue))
                    }
                    options={item.options ?? null}
                    onSave={(newValue) => handleFieldUpdate(item.field, item.field === 'email' ? newValue : (typeof newValue === 'string' ? capitalizeFirstLetter(newValue) : newValue))}
                    isEditing={isEditing}
                    type={item.type ?? 'text'}
                    readOnly={item.readOnly ?? false}
                    multiline={item.multiline ?? false}
                    renderValue={
                      item.renderValue // First check if a custom renderValue is provided
                        ? item.renderValue
                        : item.field === 'created_by_name' || item.field === 'updated_by_name'
                          ? (value) => (
                            <MDTypography
                              component="span"
                              onClick={() => {
                                const userId = item.field === 'created_by_name'
                                  ? deal?.created_by
                                  : deal?.updated_by;
                                if (userId) {
                                  navigate(`/users/${userId}/profile`);
                                }
                              }}
                              variant="button"
                              color="info"
                              fontWeight="medium"
                              sx={{
                                cursor: 'pointer'
                              }}
                            >
                              {capitalizeFirstLetter(value)}
                            </MDTypography>
                          )
                          : item.field === 'estimated_closure_date'
                            ? (value) => (value ? formatDateNoTime(value) : '')
                            : item.field === 'probability'
                              ? (value) => (value ? `${value}%` : '')
                              : item.field.includes('_at') || item.field === 'last_activity'
                                ? (value) => formatDate(value, 'datetime')
                                : undefined
                    }
                  />
                )}
              </Grid>
            );
          })}
      </Grid>
      {renderContent && (
        <MDBox mt={3}>
          {renderContent}
        </MDBox>
      )}
      </>
    );
    return (
      <UiDetailSection
        title={title}
        legacy={
          <MDBox
            mb={3}
            p={3}
            borderRadius="lg"
            sx={{
              backgroundColor: 'white',
              boxShadow: 3,
            }}
          >
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6" fontWeight="medium" color="dark">
                {title}
              </MDTypography>
            </MDBox>
            {sectionBody}
          </MDBox>
        }
      >
        {sectionBody}
      </UiDetailSection>
    );
  };

  const SearchableDropdown = ({
    options,
    value,
    label,
    onSave,
    isEditing,
    renderValue
  }) => {
    const [inputValue, setInputValue] = useState('');
    const [selectedValue, setSelectedValue] = useState(value);

    // Filter options based on input
    const filteredOptions = useMemo(() => {
      if (!inputValue) {
        // Show only first 5 options when no input
        return options.slice(0, 5);
      }

      // Filter options that match the input
      const searchInput = inputValue.toLowerCase();
      return options
        .filter(option =>
          option.searchText.includes(searchInput) ||
          option.label.toLowerCase().includes(searchInput)
        )
        .slice(0, 10); // Limit to 10 results
    }, [options, inputValue]);

    // Handle save action
    const handleSave = (newValue) => {
      setSelectedValue(newValue);
      onSave(newValue);
    };

    // Render view mode (when not editing)
    if (!isEditing) {
      return renderValue ? renderValue(selectedValue) : (
        <Typography>
          {options.find(opt => opt.value === selectedValue)?.label || ' '}
        </Typography>
      );
    }

    return (
      <Autocomplete
        value={selectedValue}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={(event, newValue) => {
          handleSave(newValue?.value || newValue);
        }}
        options={filteredOptions}
        getOptionLabel={(option) => option.label}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            fullWidth
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            {option.label}
          </li>
        )}
        isOptionEqualToValue={(option, value) =>
          option.value === value || option.value === value?.value
        }
        filterOptions={(x) => x}
        freeSolo
      />
    );
  };

  const renderCustomFields = () => {
    if (!customFields || customFields.length === 0) {
      return null;
    }
    return (
      <CustomFieldsSection
        ref={customFieldsRef}
        objectType="deals"
        objectData={deal}
        isEditing={isEditing}
        onFieldUpdate={(field, value) => {
          handleFieldUpdate(field, value);
        }}
        onError={(field, error) => {
          if (error) {
            handleFieldError(field, error);
          }
        }}
        formErrors={formErrors}
      />
    );
  };


  useEffect(() => {
    if (Object.keys(formErrors).length > 0) {
      console.log('Form errors updated:', formErrors);
    }
  }, [formErrors]);

  // Add this inside useEffect that fetches deal data or create a new useEffect
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await axiosInstance.get(`/custom-fields/deals`);
        setCustomFields(response.data || []);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        setCustomFields([]);
      }
    };

    fetchCustomFields();
  }, []);

  // Fetch global preferences for section order/hidden
  useEffect(() => {
    const fetchGlobalPreferences = async () => {
      try {
        const res = await axiosInstance.get('/preferences/global');
        const prefs = res.data?.section_preferences?.deal;
        if (prefs) {
          if (prefs.order) setSectionOrder(prefs.order);
          if (prefs.hidden) setHiddenSections(prefs.hidden);
        } else {
          setSectionOrder([
            'Deal Information',
            'Related Information',
            'Custom Field Information',
            'Audit Information',
          ]);
          setHiddenSections([]);
        }
      } catch (err) {
        setSectionOrder([
          'Deal Information',
          'Related Information',
          'Custom Field Information',
          'Audit Information',
        ]);
        setHiddenSections([]);
      } finally {
        setLoadingSections(false);
      }
    };
    fetchGlobalPreferences();
    // eslint-disable-next-line
  }, []);

  const visibleSections = sectionOrder.filter(key => !hiddenSections.includes(key));
  const { currencies } = useCurrencyCodes();

  const currencyOptions = useMemo(() => {
    const opts = (currencies || []).map((c) => ({
      value: c.code,
      label: `${c.name} (${c.code})`,
    }));
    // Keep the deal's saved currency selectable even if it isn't in the list
    const saved = deal?.currency;
    if (saved && !opts.some((o) => o.value === saved)) {
      opts.unshift({ value: saved, label: saved });
    }
    return opts;
  }, [currencies, deal?.currency]);


  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <DataLoadingSpinner
          entityType="deal"
          title="Loading Deal Data"
          message="Please wait while we fetch the deal information..."
        />

      </DashboardLayout>
    );
  }

  // Check if the user has permission to access this lead
  if (isUnauthorized) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <UnauthorizedAccess />

      </DashboardLayout>
    );
  }


  // Shared by the classic and v2 layouts below: the same actions, stage
  // dialogs, field sections and side column render in both.
  const renderDealActions = (aiTone) => (
    <>
      {/* Renders nothing unless the tenant has the AI Assistant. */}
      {deal?.id ? <AiScoreChip objectApiName="deals" recordId={deal.id} tone={aiTone} /> : null}
      <DealDetailButtons
        deal={deal}
        isEditing={isEditing}
        handleEditToggle={handleEditToggle}
        handleCloneDeal={handleCloneDeal}
        handleOpenDeleteModal={handleOpenDeleteModal}
        handleSaveChanges={handleSaveChanges}
        handleDiscardChanges={handleDiscardChanges} // Pass the discard function
        handleCreateOrder={handleCreateOrder}
        setIsEditing={setIsEditing}
        isEditLoading={isEditLoading}
        isCloneLoading={isCloneLoading}
        isDeleteLoading={isDeleteLoading}
        isSaveLoading={isSaveLoading}
        isOrderLoading={isOrderLoading}
        handleToggleEmailModal={handleToggleEmailModal}
      />
    </>
  );
  const dealStageDialogs = (
    <>
      <FinalStageWarningModal />
      <StatusStepperConfirmDialog
        open={stageStepConfirm.open}
        title="Change deal stage"
        message={
          stageStepConfirm.index != null
            ? `Are you sure you want to mark this deal as ${dealStages[stageStepConfirm.index]}?`
            : ''
        }
        loading={isDetailsLoading}
        onCancel={handleStageStepConfirmCancel}
        onConfirm={handleStageStepConfirm}
      />
      <ClosedLostModal
        isOpen={showClosedLostModal}
        onClose={() => {
          setShowClosedLostModal(false);
          setClosedLostComment(''); // Reset comment when closing
        }}
        comment={closedLostComment}
        onCommentChange={setClosedLostComment}
        onSubmit={handleClosedLostSubmit}
      />

      {/* Closed Won Confirmation Modal (legacy quotations only) */}
      {showLegacyQuotations && (
      <ClosedWonConfirmModal
          isOpen={showClosedWonModal}
          summary={quotationsSummary}
          loading={isClosedWonSaving}
          onClose={() => {
            setShowClosedWonModal(false);
            setPendingClosedWonChange(null);
          }}
          onConfirm={handleClosedWonSubmit}
      />
      )}
    </>
  );
  const dealDetailsBody = (
    <>
      {isDetailsLoading || loadingSections ? (
        <DealDetailsSkeleton />
      ) : (
        <>
          {visibleSections.map(sectionLabel => {
            if (sectionLabel === 'Deal Information') {
              return renderSection('Deal Information', [
                { label: "Name", field: "name" },
                { label: "Stage", field: "stage", options: dealStages },
                {
                  label: "Estimated Deal Value",
                  field: "value",
                  type: "number",
                  renderValue: (value) => {
                    if (!value || value === 0) return '';
                    const dealCurrency = deal?.currency || systemCurrency;
                    return formatCurrency(value, dealCurrency);
                  }
                },
                {
                  label: "Currency",
                  field: "currency",
                  options: currencyOptions,
                  renderValue: (value) => value || '',
                },
                {
                  label: "Final Deal Value",
                  field: "final_value",
                  dummyValue: (() => {
                    const acceptedQuotation = quotations.find(
                      quotation => quotation.status === 'Accepted'
                    );

                    if (acceptedQuotation) {
                      const value = parseFloat(acceptedQuotation.subtotal || 0);
                      return !isEditing
                        ? formatCurrency(value, deal?.currency || systemCurrency)
                        : value;
                    }

                    return !isEditing ? '' : 0;
                  })(),
                  readOnly: true,
                },
                { label: "Probability", field: "probability" },
                { label: "Estimated Closure Date", field: "estimated_closure_date", type: "date" },
                { label: "Deal Closure Date", field: "updated_at", show: deal?.stage === "Closed Won" || deal?.stage === "Closed Lost" },
                { label: "Description", field: "description", multiline: true },
                { label: "Closed Lost Reason", field: "closed_lost_comment", multiline: true },
              ]);
            }
            if (sectionLabel === 'Related Information') {
              return renderSection('Related Information', [
                { label: "Account", field: "account_id", options: accountagents.map(a => ({ value: a.id, label: a.name })) },
                ...(deal?.lead_id ? [{ label: "Lead:", field: "lead_id", readOnly: true }] : []),
                { label: "Contact", field: "contact_id", options: loadingAccountContacts ? [] : accountContacts.map(contact => ({ value: contact.id, label: contact.first_name ? `${contact.first_name} ${contact.last_name}` : contact.last_name })) },
                { label: "Assigned To", field: "assigned_to_name", options: agents.map(a => ({ value: String(a.id), label: capitalizeFirstLetter(a.fullname || a.username) })) },
              ]);
            }
            if (sectionLabel === 'Custom Field Information') {
              return customFields && customFields.length > 0 ? renderSection('Custom Field Information', [], renderCustomFields()) : null;
            }
            if (sectionLabel === 'Audit Information') {
              return renderSection('Audit Information', [
                { label: "Created By", field: "created_by_name", readOnly: true },
                { label: "Modified By", field: "updated_by_name", readOnly: true },
                { label: "Created Date", field: "created_at", readOnly: true },
                { label: "Modified Date", field: "updated_at", readOnly: true },
              ]);
            }
            return null;
          })}
          <Divider sx={{ my: 3 }} />
        </>
      )}
    </>
  );
  // Side-column pieces, named so the v2 record layout can place them in its
  // tabs and side column; dealSideColumn below keeps the classic order.
  const dealNbaCard = (
    <>
      {/* Renders nothing unless the user may use the AI Assistant. */}
      {deal?.id ? (
        <NextBestActions
          objectApiName="deals"
          recordId={deal.id}
          canAddTask={hasTasksModule}
          onTaskCreated={() => fetchActivities()}
        />
      ) : null}
    </>
  );

  // Activities (needs the Tasks module).
  const dealActivitiesCard = (
    <>
      {hasTasksModule && (
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={3}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EventIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Activities
            </MDTypography>
          </Box>
          <MDButton variant="gradient" color="dark" onClick={handleOpenActivityModal}>
            Add Activity
          </MDButton>
        </MDBox>
        <MDBox p={2}>
          {isActivitiesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} color="info" />
            </Box>
          ) : (
            <Activities
              activities={activities.filter(Boolean)}
              onDelete={handleDeleteActivity}
              onEdit={handleEditActivity}
            />
          )}
        </MDBox>
      </Card>
      )}
    </>
  );

  // Attachments.
  const dealAttachmentsCard = (
    <>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AttachmentIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Attachments
            </MDTypography>
          </Box>
          <MDButton variant="gradient" color="dark" onClick={() => setIsAttachmentModalOpen(true)}>
            Add Attachment
          </MDButton>
        </MDBox>
        <MDBox p={2}>
          <Attachments
            attachments={attachments}
            onDelete={handleDeleteAttachment}
            onDownload={handleDownloadAttachment}
          />
        </MDBox>
      </Card>
    </>
  );

  // Upload dialog for Attachments.
  const dealAttachmentUploadModal = (
    <>
      <AttachmentUploadModal
        isOpen={isAttachmentModalOpen}
        onClose={() => setIsAttachmentModalOpen(false)}
        onUpload={handleUploadSuccess}
        axiosInstance={axiosInstance}
        // allowedFileTypes="*/*"
        maxSizeInMB={5}
      />
    </>
  );

  // Echo feed.
  const dealEchoFeedCard = (
    <>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={3}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RecordVoiceOverIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Echo Feed
            </MDTypography>
          </Box>
          <MDButton
            variant="gradient" color="dark"
            onClick={handleOpenEchoModal}
          >
            Add Message
          </MDButton>
        </MDBox>

        <MDBox p={2}>
          {/* EchoComposer removed, replaced by modal */}
          <Modal
            open={isEchoModalOpen}
            onClose={handleCloseEchoModal}
            aria-labelledby="echo-modal-title"
            aria-describedby="echo-modal-description"
          >
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 600,
              bgcolor: 'background.paper',
              boxShadow: 24,
              borderRadius: 2,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <MDTypography id="echo-modal-title" variant="h6" mb={2}>
                Post a Feed
              </MDTypography>
              <EchoComposer
                onSubmit={handleEchoSubmit}
                onCancel={handleCloseEchoModal}
                placeholder="Type @ to mention users..."
                axiosInstance={axiosInstance}
                objectType="deals"
                objectId={id}
                onSuccess={(message) => {
                  setSnackbar({
                    open: true,
                    message: 'Feed item created successfully',
                    severity: 'success',
                  });
                  handleCloseEchoModal();
                }}
                onError={(message) => setSnackbar({
                  open: true,
                  message: `Error creating feed item: ${message}`,
                  severity: 'error',
                })}
              />
            </Box>
          </Modal>
          {/* Display feed items */}
          <Box mt={3}>
            {isFeedLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} color="info" />
              </Box>
            ) : feedItems.length > 0 ? (
              <Box className="pearl-echo-feed" sx={{
                maxHeight: 400,
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fafafa'
              }}>
                {feedItems.map((item, index) => (
                  <Box
                    key={item.id}
                    sx={{
                      p: 2,
                      borderBottom: index < feedItems.length - 1 ? '1px solid #e0e0e0' : 'none',
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: '#f8f9fa'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <MDUserAvatar
                        firstName={extractNameParts(item.created_by_user, item.created_by_name).firstName}
                        middleName={extractNameParts(item.created_by_user, item.created_by_name).middleName}
                        lastName={extractNameParts(item.created_by_user, item.created_by_name).lastName}
                        avatarUrl={extractNameParts(item.created_by_user, item.created_by_name).avatarUrl}
                        size={36}
                        variant="circular"
                        sx={{ mr: 2, flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0, ml: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight="medium"
                              sx={{
                                color: 'primary.main',
                                cursor: 'pointer',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={() => {
                                // Navigate to user profile
                                const userMention = item.feeditem.mentions?.find(m => m.type === 'user' && m.entity_name === item.created_by_name);
                                if (userMention) {
                                  navigate(`/users/${userMention.entity_id}/profile`);
                                }
                              }}
                            >
                              {item.created_by_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              {formatDate(item.updated_at, 'datetime')}
                            </Typography>
                          </Box>

                          {/* Edit/Delete buttons - only show for current user's messages */}
                          {item.created_by === user?.id && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Edit message" arrow>
                                <MDButton
                                  size="small"
                                  variant="text"
                                  color="info"
                                  onClick={() => startEditFeedItem(item)}
                                  disabled={isEditingFeed}
                                  sx={{ minWidth: 'auto', p: 0.5 }}
                                >
                                  <Edit fontSize="small" />
                                </MDButton>
                              </Tooltip>
                              <Tooltip title="Delete message" arrow>
                                <MDButton
                                  size="small"
                                  variant="text"
                                  color="error"
                                  onClick={() => handleDeleteConfirm(item)}
                                  disabled={isEditingFeed}
                                  sx={{ minWidth: 'auto', p: 0.5 }}
                                >
                                  <Delete fontSize="small" />
                                </MDButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>

                        {editingFeedItem === item.id ? (
                          <Box sx={{ mt: 1 }}>
                            <EchoComposer
                              value={editFeedContent}
                              onChange={setEditFeedContent}
                              placeholder="Edit your message..."
                              axiosInstance={axiosInstance}
                              objectType="deals"
                              objectId={id}
                              feedItemId={item.id}
                              isEditMode={true}
                              onSubmit={handleEditFeedItem}
                              onCancel={cancelEditFeedItem}
                              onSuccess={() => {
                                // Auto-save when user clicks the Update button in EchoComposer
                                setEditingFeedItem(null);
                                setEditFeedContent('');
                              }}
                              sx={{
                                mb: 1,
                                '& .MuiInputBase-root': {
                                  backgroundColor: 'white'
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              lineHeight: 1.5,
                              color: 'text.primary'
                            }}
                          >
                            {parseFeedContent(item)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mt: -2,
              }}>
                <Box sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.04)',
                  mb: 1.5
                }}>
                  <RecordVoiceOverIcon sx={{ color: 'text.secondary', fontSize: 24, }} />
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  No messages yet
                </Typography>
                 {/* <Typography variant="caption">
                  Start a conversation by typing a message above
                </Typography> */}
              </Box>
            )}
          </Box>
        </MDBox>
      </Card>
    </>
  );

  // Quotations (not shown when the Travel look is on).
  const dealQuotationsCard = (
    <>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RequestQuote sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Quotations
            </MDTypography>
          </Box>
          <MDButton variant="gradient" color="dark" onClick={handleOpenQuotationModal}>
            Add Quotation
          </MDButton>
        </MDBox>
        <QuotationsList
          quotations={quotations}
          onQuotationClick={(quotation) => navigate(`/quote/${quotation.id}`)}
          onDownloadPDF={handleDownloadAttachment}
          onSendEmail={handleToggleEmailModal}
          leadData={leadData}
          dealStage={deal?.stage}
        />
      </Card>
    </>
  );

  // Orders (not shown when the Travel look is on).
  const dealOrdersCard = (
    <>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ShoppingCart sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Orders
            </MDTypography>
          </Box>
          <MDButton variant="gradient" color="dark" onClick={handleCreateOrder} disabled={isOrderLoading}>
            {isOrderLoading ? <CircularProgress size={24} color="inherit" /> : 'Add Order'}
          </MDButton>
        </MDBox>
        <OrdersList
          orders={orders}
          onOrderClick={(order) => navigate(`/orders/${order.id}`)}
        />
      </Card>
    </>
  );

  const dealSideColumn = (
    <>
      {dealNbaCard}
      {dealActivitiesCard}

      {hasTasksModule && (
        <>
          <br />
          <br />
        </>
      )}

      {dealAttachmentsCard}
      {dealAttachmentUploadModal}

      <Box mt={5} />

      {dealEchoFeedCard}
      {showLegacyQuotations && (
      <>
      <Box mt={5} />
      {dealQuotationsCard}
      </>
      )}

      {showLegacyOrders && (
      <>
      <Box mt={5} />
      {dealOrdersCard}
      </>
      )}

    </>
  );

  // v2: the deal laid out like Account 360 (components/v2/record). The
  // pieces are the ones the classic page renders.
  const lastDealActivity = activities.map((a) => a?.created_at).filter(Boolean).sort().pop();
  const dealContactName = contactData ? (contactData.full_name || [contactData.first_name, contactData.last_name].filter(Boolean).join(' ')) : null;
  const v2Main = (
    <MDBox>
      <RecordPageV2
        sectionsLabel="Deal sections"
        hero={{
          label: 'Deal summary',
          icon: Briefcase,
          eyebrow: 'Deal',
          title: deal?.name || '',
          actions: renderDealActions('default'),
          badges: deal?.stage ? <StatusBadge status={deal.stage} /> : null,
          meta: [
            { key: 'owner', icon: UserRound, content: deal?.assigned_to_name ? <>Owned by <UserLink userId={deal.assigned_to} name={capitalizeFirstLetter(deal.assigned_to_name)} /></> : 'Unassigned' },
            {
              key: 'account',
              icon: Building2,
              content: accountData?.name && deal?.account_id ? (
                <Link to={`/accounts/${deal.account_id}`} style={{ color: 'inherit', fontWeight: 600 }}>{accountData.name}</Link>
              ) : null,
            },
            {
              key: 'contact',
              icon: Users,
              content: dealContactName && deal?.contact_id ? (
                <Link to={`/contacts/${deal.contact_id}`} style={{ color: 'inherit', fontWeight: 600 }}>{dealContactName}</Link>
              ) : null,
            },
          ],
        }}
        tiles={[
          // Blank when the classic field sections show it blank (empty or zero).
          { key: 'value', icon: CircleDollarSign, label: 'Value', value: Number(deal?.value) ? formatCurrency(deal.value, deal.currency) : '—' },
          { key: 'probability', icon: Percent, label: 'Probability', value: Number(deal?.probability) ? `${deal.probability}%` : '—' },
          { key: 'close', icon: CalendarDays, label: 'Expected close', value: deal?.estimated_closure_date ? formatDateNoTime(deal.estimated_closure_date) : '—' },
          showLegacyQuotations ? { key: 'quotes', icon: FileText, label: 'Quotes', value: String(quotations.length), tab: 'quotes' } : null,
          showLegacyOrders ? { key: 'orders', icon: Package, label: 'Orders', value: String(orders.length), tab: 'orders' } : null,
          { key: 'last', icon: Clock, label: 'Last activity', value: relativeTime(lastDealActivity) || 'None yet', tab: 'activity' },
        ]}
        tabs={[
          {
            key: 'overview',
            label: 'Overview',
            icon: LayoutGrid,
            render: () => (
              <TabStack>
                <Card sx={{ p: 2.5 }}>{renderStepper()}</Card>
                {/* Each field section is its own card (DetailSection), so no wrapping Card. */}
                <MDBox>{dealDetailsBody}</MDBox>
              </TabStack>
            ),
          },
          showLegacyQuotations ? { key: 'quotes', label: 'Quotes', icon: FileText, count: quotations.length, render: () => dealQuotationsCard } : null,
          showLegacyOrders ? { key: 'orders', label: 'Orders', icon: Package, count: orders.length, render: () => dealOrdersCard } : null,
          {
            key: 'activity',
            label: 'Activity',
            icon: History,
            count: hasTasksModule ? activities.filter(Boolean).length : undefined,
            render: () => (
              <TabStack>
                {dealActivitiesCard}
                {dealEchoFeedCard}
              </TabStack>
            ),
          },
          { key: 'files', label: 'Files', icon: Paperclip, count: attachments.length, render: () => dealAttachmentsCard },
        ]}
        aside={(selectTab) => (
          <>
            {dealNbaCard}
            <ActivityTimeline activities={activities} feedItems={feedItems} onOpenAll={() => selectTab('activity')} emptyText="No activity on this deal yet." />
          </>
        )}
      />
      {/* The stage dialogs (won, lost reason...) open from the stage path. */}
      {dealStageDialogs}
      {dealAttachmentUploadModal}
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isV2 ? <RecordCallProvider value={{ parentTable: "deals", parentId: deal?.id, dealId: deal?.id, accountId: deal?.account_id, contactId: deal?.contact_id }}>{v2Main}</RecordCallProvider> : (
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <MDTypography
                    variant="h6"
                    color="white"
                    sx={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      lineHeight: 1.35,
                      pr: 2,
                    }}
                  >
                    {deal?.name || ''}
                  </MDTypography>
                  <MDBox display="flex" alignItems="center" gap={1}>
                  {renderDealActions("header")}
                  </MDBox>
                </MDBox>
                <MDBox>
                  {renderStepper()}
                </MDBox>
                {dealStageDialogs}

              </MDBox>
              <MDBox p={3}>
                {dealDetailsBody}
              </MDBox>

            </Card>
          </Grid>



          <Grid item xs={12} md={4}>
            {dealSideColumn}
          </Grid>

        </Grid>
      </MDBox>
      )}


      {showLegacyQuotations && (
      <QuotationModal
        isOpen={isQuotationModalOpen}
        onClose={handleCloseQuotationModal}
        onSave={handleSaveQuotation}
        onProductsChange={handleProductsChange}
        resetForm={true}
      // demoProducts={demoProducts}
      />
      )}

      {/* Activity Modal */}

      <ActivityModal
        isOpen={isActivityModalOpen}
        onClose={handleCloseActivityModal}
        parentId={id}
        parent="deals"
        axiosInstance={axiosInstance}
        onActivitySaved={handleActivitySaved}
        currentUser={user}
      />
      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        aria-labelledby="delete-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 4,
              py: 3,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <MDTypography
              id="delete-modal-title"
              variant="h6"
              component="h2"
              sx={{ color: '#FFFFFF' }}
            >
              Delete Deal
            </MDTypography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4 }}>
            <MDTypography variant="body1" mb={3}>
              Are you sure you want to delete this deal? This action cannot be undone.
            </MDTypography>

            <MDBox display="flex" justifyContent="flex-end">
              <MDButton
                onClick={handleCloseDeleteModal}
                variant="outlined"
                color="secondary"
                sx={{ marginRight: 2 }}
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={handleDelete}
                variant="gradient"
                color="error"
              >
                Delete
              </MDButton>
            </MDBox>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>





      {/* Email Modal */}
      <EmailModal
        open={emailModalOpen}
        onClose={() => {
          setEmailModalOpen(false);
          setEmailAttachments([]);
          setSelectedQuotation(null);
        }}
        newEmail={newEmail}
        setNewEmail={setNewEmail}
        handleSend={handleSendEmail}
        isGenerating={isGeneratingEmail}
        isSending={isEmailSending} // Add this prop
        errorMessage={emailError}
        emailContext={emailContext}
        setEmailContext={setEmailContext}
        showCc={showCc}
        showBcc={showBcc}
        toggleCc={toggleCc}
        toggleBcc={toggleBcc}
        handleQuillChange={handleQuillChange}
        modules={quillModules}
        formats={quillFormats}
        loading={false}
        attachments={emailAttachments}
        setEmailAttachments={setEmailAttachments}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-confirm-dialog-title"
        aria-describedby="delete-confirm-dialog-description"
      >
        <DialogTitle id="delete-confirm-dialog-title">
          Delete Message
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this message? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleDeleteCancel} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleDeleteConfirmOk} color="error" variant="contained">
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>



    </DashboardLayout>
  );
}

export default DealDetail;